import { animate, inView, scroll, stagger, frame, cancelFrame, hover, wrap, resize, motionValue, styleEffect, transformValue, svgEffect, springValue } from "@theme/m";
import { $4, $$4, $id4, matchMediaQuery, currency, createImg, renderSrc, registerEl, debounce } from "@theme/utils";
import { Modal, Popover, Drawer, getterAdd, getterGet, isRTL } from "@theme/global";
import { ThemeEvents } from '@theme/events';
const { dialogAdded, dialogOpening, dialogOpen, dialogClosing, dialogClose, videoUnmute, loadingStart, loadingEnd, cartUpdate, cartReload, admEvts } = ThemeEvents;
import { computePosition, shift, flip, offset, arrow } from "@theme/floating";
import { overrideFocus } from "@theme/focus";

// $4 --> singular selector
// $$4 --> plural selector
// $id4 --> id selector

// $4('.aa') == document.querySelector('.aa')
// $4('.aa', section) == section.querySelector('.aa')

// $$(.aa) == document.querySelectorAll('.aa')
// $$(.aa, section) == section.querySelectorAll('.aa')

// $id4('aa') == document.getElementById('aa')

// https://motion.dev/docs/animate
// https://motion.dev/docs/inview
// https://motion.dev/docs/scroll

// Reveal Elements On Scroll
class HdtRevealElementsOnScroll {
    constructor(container, options = {}) {
        this.container = container;
        this.isRevealVertical = false;
        this.elems = $$4('[reveal], [can-reveal]', this.container).filter(elem => !this.isInsideSliderReveal(elem));
        this.defaultDir = this.container.getAttribute('default-reveal') || (isRTL ? 'right' : 'left');
        this.options = Object.assign({
            duration: 0.48,
            delay: 0.09,
            distance: '16px',
            ease: 'easeOut',
            once: true,
            direction: this.defaultDir
        }, options);

        const attrDistance = container.getAttribute('reveal-distance');
        if (attrDistance && !isNaN(parseFloat(attrDistance))) {
            this.distance = attrDistance;
        } else {
            this.distance = this.options.distance; // fallback
        }

        if (container.hasAttribute('reveal-repeat')) {
            this.options.once = false;
        }
        this.init();
    }

    isInsideSliderReveal(elem) {
      let parent = elem.parentElement;
      while (parent) {
          if (
            (parent.tagName && (
              parent.tagName.toLowerCase() === 'hdt-slider-reveal' ||
              parent.tagName.toLowerCase() === 'hdt-reval-items'
            )) ||
            parent.classList.contains('hdt-con-reveal--disabled')
          ) return true;

          if (parent.classList.contains('hdt-con-reveal--vertical')) {
            this.isRevealVertical = true;
          }
          parent = parent.parentElement;
      }
      return false;
    }

    getElemDirection(elem) {
        let dir = elem.getAttribute('reveal');
        if (!dir && elem.hasAttribute('can-reveal')) {
            dir = this.defaultDir;
        }
        dir = dir.trim().toLowerCase();
        if (dir && ['top', 'bottom', 'left', 'right'].includes(dir)) {
          if (this.isRevealVertical && ['left', 'right'].includes(dir)) {
            dir = 'top';
          }
          if (isRTL) {
            dir = dir == 'left' ? 'right' : dir == 'right' ? 'left' : dir;
          }
          return dir;
        }
        return this.options.direction;
    }

    getTransformVals(direction, distance, forInitial = true) {
        const d = parseFloat(distance);
        if (this.isRevealVertical && ['left', 'right'].includes(direction)) {
          direction = 'top';
        }
        switch (direction) {
          case 'top': return { x: 0, y: forInitial ? -d : 0 };
          case 'bottom': return { x: 0, y: forInitial ? d : 0 };
          case 'left': return { x: forInitial ? -d : 0, y: 0 };
          case 'right': return { x: forInitial ? d : 0, y: 0 };
          default: return { x: forInitial ? -d : 0, y: 0 }; // left
        }
    }

    setElemInitialState(elem, i) {
        // Repeat mode: left the screen while revealing, stop that animation first
        if (elem._hdtReveal) {
          const running = elem._hdtReveal;
          elem._hdtReveal = null;
          running.cancel?.();
        }
        const direction = this.getElemDirection(elem);
        const { x, y } = this.getTransformVals(direction, this.distance, true);
        elem.style.opacity = '0';
        elem.style.clipPath = '';
        elem.style.transform = `translate(${x}px, ${y}px)`;
    }

    revealElem(elem, i) {
        const direction = this.getElemDirection(elem);
        const { x, y } = this.getTransformVals(direction, this.distance, true);

        let delay = i * this.options.delay;
        if (i > 6) {
          if (i < 10) {
              delay = 6 * this.options.delay + i * 0.2 * this.options.delay;
          } else {
              delay = 8 * this.options.delay;
          }
        }

        let opacityTo = elem.hasAttribute('aria-disabled') ? 0.5 : 1;

        // A transform string (not x / y) runs as a compositor animation instead of a JS update every frame
        const controls = animate(
          elem,
          { opacity: [0, opacityTo], transform: [`translate(${x}px, ${y}px)`, 'translate(0px, 0px)'] },
          { duration: this.options.duration, delay: delay, ease: this.options.ease, fill: 'forwards' }
        );
        elem._hdtReveal = controls;
        // Keep the end state inline (same as before) and let the animation go
        controls.then(() => {
          if (elem._hdtReveal !== controls) return;
          elem._hdtReveal = null;
          elem.style.opacity = opacityTo;
          elem.style.transform = 'translate(0px, 0px)';
          controls.cancel?.();
        }, () => {});
    }

    init() {
        this._stopFns = [];
        // One IntersectionObserver for every element of the container (inView observes the whole list)
        const indexes = new Map();
        this.elems.forEach((elem, i) => {
            this.setElemInitialState(elem, i);
            indexes.set(elem, i);
        });
        if (!this.elems.length) return;
        const stop = inView(
          this.elems,
          (element, enterInfo) => {
            const i = indexes.get(element);
            this.revealElem(element, i);
            if (!this.options.once) {
              return (leaveInfo) => {
                this.setElemInitialState(element, i);
              };
            }
          },
          { amount: 0.1 }
        );
        this._stopFns.push(stop);
    }

    destroy() {
        if (this._stopFns) {
            this._stopFns.forEach(stop => {
              try { stop && stop(); } catch (e) {}
            });
            this._stopFns = [];
        }
    }
}

function hdtInitRevealOnScroll(elem) {
    if (elem._hdtRevealInstance) elem._hdtRevealInstance.destroy();
    elem._hdtRevealInstance = new HdtRevealElementsOnScroll(elem);
}

function hdtInitAllRevealOnScroll(context = document) {
  if (matchMediaQuery("motion")) $$4('[reveal-on-scroll]', context).forEach(hdtInitRevealOnScroll);
}

hdtInitAllRevealOnScroll();

if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener(admEvts.seLoad, function(e) {
        hdtInitAllRevealOnScroll(e.target);
    });

    document.addEventListener(admEvts.seUnload, function(e) {
        $$4('[reveal-on-scroll]', e.target).forEach(elem => {
            if (elem._hdtRevealInstance) {
                elem._hdtRevealInstance.destroy();
                elem._hdtRevealInstance = null;
            }
        });
    });
}

class HdtAlwaysShowHc extends HTMLElement {
  constructor() {
    super();
    this.hotspots = [];
    this._onClick = this._onClick.bind(this);
    this._onOutsideClick = this._onOutsideClick.bind(this);
    this._observer = null;
    this._active = true;
    this._popoverChecked = false;
    this._updateQueued = false;
    this._checkOverflow = false;
    this._controller = null;
    this._ro = null;
    this.isLayered = false;
    this.container = null;
    this.hotspotsArea = null;
  }

  connectedCallback() {
    this.hotspots = [...$$4('.hdt-hotspot', this)];
    if (!this.hotspots.length) return;
    this._controller = new AbortController();
    const { signal } = this._controller;
    this.isLayered = this.classList.contains('hdt-always-show-hc--layered');
    this.container = $4('.hdt-image-hotspots-container', this);
    this.hotspotsArea = $4('.hdt-hotspots-area', this);
    this._active = true;
    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', this._onClick);
    });
    document.addEventListener('click', this._onOutsideClick, { signal });
    this._observer = new IntersectionObserver(this._onVisibilityChange.bind(this), {
      threshold: 0,
    });
    this._observer.observe(this);
    // The first observer callbacks run the first measure (no forced layout on connect).
    // Also covers window resizes: every size the metrics depend on is observed
    this._ro = new ResizeObserver(() => this._scheduleUpdate());
    this._ro.observe(this);
    if (this.container) this._ro.observe(this.container);
    if (this.hotspotsArea) this._ro.observe(this.hotspotsArea);

    if (window.Shopify && Shopify.designMode) {
      this._setupShopifyBlockSelection(signal);
    }
  }

  disconnectedCallback() {
    this.hotspots.forEach(hotspot => {
      hotspot.removeEventListener('click', this._onClick);
    });
    this._controller?.abort();
    if (this._observer) this._observer.disconnect();
    if (this._ro) this._ro.disconnect();
    this._updateQueued = false;
  }

  // Batch the observer callbacks of a frame into one measure: read every rect first (frame.read), then write (frame.render)
  _scheduleUpdate(checkOverflow = false) {
    if (checkOverflow) this._checkOverflow = true;
    if (this._updateQueued) return;
    this._updateQueued = true;
    frame.read(() => {
      this._updateQueued = false;
      if (!this.isConnected) return;
      const data = this._measure();
      frame.render(() => {
        this._apply(data);
        if (!this._checkOverflow) return;
        this._checkOverflow = false;
        // Popover overflow is checked once the metrics are applied (next frame)
        frame.read(() => {
          const overflow = this._measureOverflow();
          frame.render(() => overflow.forEach(({ popover, top, bottom }) => {
            if (top) popover.classList.add('hdt-is-over-top');
            if (bottom) popover.classList.add('hdt-is-over-bottom');
          }));
        });
      });
    });
  }

  _measure() {
    const restores = [],
    measurable = (el) => {
      const restore = this._ensureMeasurable(el);
      if (restore) restores.push(restore);
    },
    { container, hotspotsArea } = this,
    items = this.hotspots.map(hotspot => ({
      hotspot,
      btn: $4('.hdt-hotspot__btn', hotspot),
      popover: $4('.hdt-hotspot__popover', hotspot)
    })).filter(({ btn, popover }) => btn && popover);

    if (container && hotspotsArea) {
      measurable(container);
      measurable(hotspotsArea);
    }
    items.forEach(({ popover }) => measurable(popover));

    const distance = container && hotspotsArea
      ? Math.max(0, (container.getBoundingClientRect().width - hotspotsArea.getBoundingClientRect().width) / 2)
      : null,
    metrics = items.map(({ hotspot, btn, popover }) => ({
      hotspot,
      btnRect: btn.getBoundingClientRect(),
      popRect: popover.getBoundingClientRect()
    }));

    restores.reverse().forEach(restore => restore());
    return { distance, metrics };
  }

  _apply({ distance, metrics }) {
    if (distance !== null) this.style.setProperty('--hsa-distance', `${distance.toFixed(2)}px`);
    metrics.forEach(({ hotspot, btnRect, popRect }) => {
      const btnCx = btnRect.left + btnRect.width / 2;
      const btnCy = btnRect.top + btnRect.height / 2;
      const popCx = popRect.left + popRect.width / 2;
      const popCy = popRect.top + popRect.height / 2;
      const dxRaw = popCx - btnCx;
      const dy = popCy - btnCy;
      const dxRawAbs = Math.abs(dxRaw);
      const sign = dxRaw < 0 ? -1 : 1;
      const minAbs = btnRect.width;
      const dxAbs = Math.max(dxRawAbs, minAbs);
      const dx = sign * dxAbs;
      const dyAbs = Math.abs(dy);

      hotspot.style.setProperty('--hs-dx', `${dx.toFixed(2)}px`);
      hotspot.style.setProperty('--hs-dy', `${dy.toFixed(2)}px`);
      hotspot.style.setProperty('--hs-dx-abs', `${dxAbs.toFixed(2)}px`);
      hotspot.style.setProperty('--hs-dy-abs', `${dyAbs.toFixed(2)}px`);
      hotspot.style.setProperty('--hs-popover-w', `${popRect.width.toFixed(2)}px`);
      hotspot.style.setProperty('--hs-popover-h', `${popRect.height.toFixed(2)}px`);

      hotspot.classList.toggle('hdt-small-line-1', dxAbs < btnRect.width + btnRect.width);
      hotspot.classList.toggle('hdt-negative-dir-line-1', sign < 0);
      hotspot.classList.toggle('hdt-positive-dir-line-1', sign >= 0);
    });
  }

  _onClick(e) {
    if (!this._active) return;
    const clicked = e.currentTarget;
    this.hotspots.forEach(h => h.classList.remove('hdt-hotspot--active'));
    clicked.classList.add('hdt-hotspot--active');
  }

  _onOutsideClick(e) {
    if (!this._active) return;
    const isInside = this.hotspots.some(hotspot => hotspot.contains(e.target));
    if (!isInside) {
      this.hotspots.forEach(h => h.classList.remove('hdt-hotspot--active'));
    }
  }

  _onVisibilityChange(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this._scheduleUpdate(!this._popoverChecked);
        this._popoverChecked = true;
        this._reactivate();
      } else {
        this._deactivate();
      }
    });
  }

  _reactivate() {
    if (this._active) return;
    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', this._onClick);
    });
    this._active = true;
  }

  _deactivate() {
    if (!this._active) return;
    this.hotspots.forEach(hotspot => {
      hotspot.removeEventListener('click', this._onClick);
      hotspot.classList.remove('hdt-hotspot--active');
    });
    this._active = false;
  }

  _ensureMeasurable(el) {
    const cs = window.getComputedStyle(el);
    if (cs.display !== 'none' && cs.visibility !== 'hidden') return null;
    const prev = {
      display: el.style.display,
      visibility: el.style.visibility,
      transform: el.style.transform,
      position: el.style.position,
    };
    el.style.display = 'block';
    el.style.visibility = 'hidden';
    el.style.transform = 'translateZ(0)';
    if (cs.position === 'static') el.style.position = 'relative';
    return () => {
      el.style.display = prev.display;
      el.style.visibility = prev.visibility;
      el.style.transform = prev.transform;
      el.style.position = prev.position;
    };
  }

  _measureOverflow() {
    if (!this.hotspotsArea || !this.isConnected) return [];
    const hotspotsAreaRect = this.hotspotsArea.getBoundingClientRect();
    return this.hotspots.map(hotspot => {
      const popover = $4('.hdt-hotspot__popover', hotspot);
      if (!popover) return null;
      const popoverRect = popover.getBoundingClientRect();
      return {
        popover,
        top: popoverRect.top < hotspotsAreaRect.top,
        bottom: popoverRect.bottom > hotspotsAreaRect.bottom
      };
    }).filter(Boolean);
  }

  _setupShopifyBlockSelection(signal) {
    document.addEventListener(admEvts.select, event => {
      const block = event.target;
      const hotspot = block.closest('.hdt-hotspot');
      if (hotspot) {
        hotspot.classList.add('hdt-hotspot--active');
      }
    }, { signal });
    document.addEventListener(admEvts.deselect, event => {
      const block = event.target;
      const hotspot = block.closest('.hdt-hotspot');
      if (hotspot) {
        hotspot.classList.remove('hdt-hotspot--active');
      }
    }, { signal });
  }
}
registerEl('hdt-always-show-hc', HdtAlwaysShowHc);

class PopoverAnimate extends Popover {
  animateDialogOpen() {
    if (this._openedAsModal) {
      return super.animateDialogOpen();
    } else {
      this.updatePos(this.dialog.btnOpening, this.dialog, this.arrowEl, this.placement);
      // const sequence = [
      //   [ this.dialog, { top: ["10px", "0"], opacity: [0, 1], visibility: ["hidden", "visible"], '--inset-popover': ["50%", "0%"], '--popover-radius': ["40px", "0px"] }, { duration: 0.4 } ],
      //   [ $4('.hdt-popover__arrow', this), { opacity: [0, 1], transform: [`rotate(45deg) translateY(15px)`, `rotate(45deg) translateY(0)`] }, { at: ">", duration: 0.1 } ],
      // ];
      // --hdt-popover-y drives `translate` (popover-animate-styles) instead of `top`: no layout each frame, and it adds to the positioning transform set by updatePos
      return animate(this.dialog, { '--hdt-popover-y': ["10px", "0px"], opacity: [0, 1], visibility: ["hidden", "visible"], '--inset-popover': ["50%", "0%"], '--popover-radius': ["40px", "0px"] }, { duration: 0.4 });
    }
  }

  animateDialogClose() {
    if (this._openedAsModal) {
      return super.animateDialogClose();
    } else {
      return animate(this.dialog, { '--hdt-popover-y': ["0px", "10px"], opacity: [1, 0], visibility: ["visible", "hidden"], '--inset-popover': ["0%", "50%"], '--popover-radius': ["0px", "40px"] }, { duration: 0.25 });
    }
  }
}
registerEl("hdt-popover-animate", PopoverAnimate);

class ModalStory extends Modal {
  #controller;
  #warmer = null;
  #sectionInView = false;
  #modalOpen = false;
  #cardVideos = null;
  #syncQueued = false;
  constructor() {
    super();
    this._animating = false;
    this._phase = null;
    this._ghost = null;
    this._openTriggerEl = null;
    this._openPsiEl = null;
    this._openPsiRectSnapshot = null;
    this._openToken = null;
    this._openSeq = 0;
    this._productsStorySlider = null;
    this._radiusCache = new Map();
    this._marqueeCompensationPx = 25.5;
    this._marqueeAutoConfigured = null;
    this._marqueeHold = false;
    this._marqueeGuardAbort = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    let controller;
    const nav_slidestory = $4('.hdt-story-nav', this),
    _signal = this.#controller.signal,
    btnSound = $4('hdt-sound-video[current]', this),
    sliderButtons = $$4('.hdt-indicators--products-story button', this);

    // Opening a story: start loading its video on intent (hover, focus, press) so the modal video starts fast
    const cards = this.previousElementSibling;
    if (cards) {
      let warmTimer, warmPending;
      const cardOf = (evt) => evt.target.closest?.('hdt-control-modal-story[has-video]'),
      cancelWarm = () => { clearTimeout(warmTimer); warmPending = null; },
      warmNow = (trigger) => { cancelWarm(); this.#warmStory(trigger); };
      // Mouse passing over the row: wait until it rests on a card, so sweeping across does not start and drop a download per card
      cards.addEventListener('pointerover', (evt) => {
        const trigger = cardOf(evt);
        if (evt.pointerType !== 'mouse') return warmNow(trigger);
        // Moving between elements of the same card keeps the pending timer
        if (trigger === warmPending) return;
        cancelWarm();
        if (!trigger) return;
        warmPending = trigger;
        warmTimer = setTimeout(() => warmNow(trigger), 150);
      }, { signal: _signal });
      // Focus or press: the visitor is about to open it, start at once
      ['focusin', 'pointerdown'].forEach((type) => {
        cards.addEventListener(type, (evt) => warmNow(cardOf(evt)), { signal: _signal });
      });
      _signal.addEventListener('abort', cancelWarm);
      // Mouse left the cards without opening a story: stop the download (touch fires pointerleave before the click)
      cards.addEventListener('pointerleave', (evt) => {
        if (evt.pointerType !== 'mouse') return;
        cancelWarm();
        if (!this.#modalOpen) this.#releaseWarmer();
      }, { signal: _signal });
    }

    this.dialog.addEventListener(dialogOpening, ()=> {
      this.#modalOpen = true;
      $$4('hdt-video-hover[playing]', this.previousElementSibling).forEach( (el) => el.pause() );
      const story = $4('.hdt-slider--story', this),
      // Opened without a card (theme editor block select, or from code): start at the first story
      slide = this.dialog.btnOpening?.parentElement,
      index = parseInt(slide?.dataset.index) || 0;
      sliderButtons.forEach((btn, i) => btn.setAttribute('aria-current', index == i));
      story?.reInit();
      story?.goToIndex(index, true);

      if (!slide?.hasAttribute('has-video')) return;
      const video = $4('video', slide);
      btnSound?.toggleAttribute('hidden', !video);
      // Sound follows the opened story: unmuting here mutes the card video via videoUnmute
      if (video) btnSound?.setMuted(video.muted);
    }, { signal: _signal });

    this.dialog.addEventListener(dialogOpen, ()=> {
      // Open animation interrupted by a close: the late dialogOpen must not start the modal videos
      if (!this.dialog.open) return;
      // The modal video loads on its own now (from the warmed cache)
      this.#releaseWarmer();
      controller = new AbortController();
      const quickView = $id4('hdt-quick-view-modal'),
      story = $4('.hdt-slider--story', this),
      { signal } = controller;

      quickView?.addEventListener('dialog:ajax', () => {
        story?.pause();
      }, { signal });

      quickView?.addEventListener(dialogOpen, () => {
        story?.pause();
        $$4('hdt-video[playing]', story).forEach( (el) => el.pause() );
        nav_slidestory.style.setProperty('--hdt-modal-state','paused');
      }, { signal });

      quickView?.addEventListener(dialogClose, () => {
        story?.play();
        $4('.is-selected hdt-video', story)?.play();
        nav_slidestory.style.setProperty('--hdt-modal-state','running');
      }, { signal });

      if (story.apiS) {
        this.#videoModalFn(story.apiS.slideNodes()[story.apiS.selectedScrollSnap()], story, btnSound);
      } else {
        story.addEventListener('reInit', (evt)=> {
          const {sliderApi} = evt.detail;
          this.#videoModalFn(sliderApi.slideNodes()[sliderApi.selectedScrollSnap()], story, btnSound);
        }, { signal });
      }
      story.addEventListener('select', (evt)=> {
        const {sliderApi} = evt.detail;
        this.#videoModalFn(sliderApi.slideNodes()[sliderApi.selectedScrollSnap()], story, btnSound);
      }, { signal });

    }, { signal: _signal });

    this.dialog.addEventListener(dialogClosing, () => {
      const story = $4('.hdt-slider--story', this),
      hdtVideo = $4('.hdt-product-story-item.is-selected hdt-video', story);
      hdtVideo?.pause();
    }, { signal: _signal });

    this.dialog.addEventListener(dialogClose, () => {
      this.#modalOpen = false;
      this.#releaseWarmer();
      controller?.abort();
      // Safety net if the close animation was skipped/interrupted
      $$4('.hdt-psi-inner', this.previousElementSibling).forEach((psi) => this._toggleSourceCard(psi, false));
      // The next open must not pick up this card from a stale class
      $$4('.hdt-just-clicked', this.previousElementSibling).forEach((el) => el.classList.remove('hdt-just-clicked'));
      btnSound?.setAttribute('hidden', '');
      this.#scheduleSync();
    }, { signal: _signal });

    if (this.hasAttribute('is--lazy')) return;
    // Autoplay "when in view": Embla slidesInView() covers the slider viewport (root = slider),
    // inView covers page scroll, so a video plays only when visible in both.
    const slider = this.previousElementSibling;
    slider?.addEventListener('slidesInView', () => this.#scheduleSync(), { signal: _signal });
    // MediaManager pauses every video on any dialogClose: resume the visible ones after it
    document.addEventListener(dialogClose, () => this.#scheduleSync(), { signal: _signal });
    const stopInView = inView(slider, () => {
      this.#sectionInView = true;
      this.#scheduleSync();
      return () => {
        this.#sectionInView = false;
        this.#scheduleSync();
      };
    });
    _signal.addEventListener('abort', stopInView, { once: true });
  }

  // Coalesce the sync requests of the same task (slidesInView, inView, dialog + document dialogClose...) into one pass.
  // Microtask => runs after every listener of the current event, e.g. after MediaManager paused all videos on dialogClose.
  #scheduleSync() {
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      this.#syncVideos();
    });
  }

  // Play the card videos visible in both the slider (Embla slidesInView) and the page, pause the others
  #syncVideos() {
    const sliderApi = this.previousElementSibling?.apiS;
    if (!sliderApi || this.hasAttribute('is--lazy')) return;
    this.#cardVideos ??= sliderApi.slideNodes().map((slide) => {
      const videoHover = slide.hasAttribute('has-video') && $4('hdt-video-hover', slide),
      video = videoHover && $4('video', videoHover);
      return video ? { videoHover, video, btnSound: $4('hdt-sound-video', slide) } : null;
    });
    // Reduced motion: no autoplay when in view, every card video stays paused
    const inViewIndexes = this.#sectionInView && !this.#modalOpen && matchMediaQuery('motion') ? new Set(sliderApi.slidesInView()) : null;
    this.#cardVideos.forEach((item, index) => {
      if (!item) return;
      if (!inViewIndexes?.has(index)) {
        item.videoHover.pause();
        // Started here with video.play(), so the component may not know it plays yet: pause the element too
        if (!item.video.paused) item.video.pause();
        return;
      }
      const { video, btnSound } = item;
      this.#playSafe(video, () => btnSound ? btnSound.setMuted(true) : (video.muted = true));
    });
  }

  // Browser may block unmuted autoplay: fall back to muted playback
  #playSafe(video, mute) {
    if (!video.paused) return;
    video.play()?.catch((err) => {
      if (err.name !== 'NotAllowedError' || video.muted) return;
      mute();
      video.play()?.catch(() => {});
    });
  }
  #onVideoReady(videoEl, callback) {
    if (videoEl.readyState >= 3) {
      callback();
    } else {
      videoEl.addEventListener('canplay', () => {
        callback();
      }, { once: true });
    }
  }
  #videoModalFn(slide, story, btnSound) {
    //console.log(slide);
    const hdtVideo = $4('hdt-video', slide);
    // Keep the selected video running (already started by the open hand-off)
    $$4('hdt-video[playing]', story).forEach( (el) => el !== hdtVideo && el.pause() );
    btnSound?.toggleAttribute('hidden', !hdtVideo);
    if (!hdtVideo) return;
    btnSound?.update(btnSound.hasAttribute('muted'));
    const video = $4('video', hdtVideo);
    this.#onVideoReady(video, () => {
      // Still the selected story? (a late canplay must not start a story the user already left)
      const api = story?.apiS;
      if (!this.dialog.open || (api && api.slideNodes()[api.selectedScrollSnap()] !== slide)) return;
      this.#playSafe(video, () => btnSound ? btnSound.setMuted(true) : (video.muted = true));
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#cardVideos = null;
    this.#releaseWarmer();
  }

  /**
   * Warm the HTTP cache with the story video of a card: a detached video element shares the media cache
   * with the modal video, which stays lazy (its own loader reloads it when the modal opens).
   */
  #warmStory(trigger) {
    if (!trigger || this.#warmer?.trigger === trigger) return;
    // The card video plays the same file: already buffered
    const cardVideo = $4('video', trigger);
    if (cardVideo && cardVideo.readyState >= 3) return;
    const slide = $4(`.hdt-slider--story .hdt-product-story-item[data-block_index="${trigger.dataset.index}"]`, this),
    video = document.createElement('video'),
    // Same source the modal video will pick: the first one matching the screen that the browser can play
    source = slide && $$4('hdt-video video source', slide).find((el) => (!el.media || matchMedia(el.media).matches) && (!el.type || video.canPlayType(el.type))),
    src = source && (source.dataset.src || source.getAttribute('src'));
    if (!src) return;
    this.#releaseWarmer();
    video.muted = true;
    video.preload = 'auto';
    video.src = src;
    this.#warmer = { trigger, video };
  }

  #releaseWarmer() {
    if (!this.#warmer) return;
    // Stop the download
    this.#warmer.video.removeAttribute('src');
    this.#warmer.video.load();
    this.#warmer = null;
  }

  get #autoScroll() {
    return this._productsStorySlider.apiS.plugins()?.autoScroll;
  }

  wait(ms) { return new Promise(r => setTimeout(r, ms)); }
  _lock(phase) { if (this._animating) return false; this._animating = true; this._phase = phase; return true; }
  _unlock() { this._animating = false; this._phase = null; }
  _rect(el) { return el.getBoundingClientRect(); }

  _getStopMarqueeFlag() {
    const val = this.getAttribute('data-stop-marquee-on-open');
    if (val == null) return true;
    const v = String(val).toLowerCase();
    return !(v === 'false' || v === '0' || v === 'no' || v === 'off');
  }

  _getPsiInner(trigger) {
    if (!trigger) return null;
    try { return $4(':scope > .hdt-psi-inner', trigger) || $4('.hdt-psi-inner', trigger); }
    catch { return $4('.hdt-psi-inner', trigger); }
  }

  async _waitForJustClicked(maxMs = 5, stepMs = 16) {
    const sel = `.hdt-just-clicked[data-target="#${this.dialog?.id}"]`;
    const t0 = performance.now();
    let el = $4(sel, document);
    while (!el && (performance.now() - t0) < maxMs) {
      await this.wait(stepMs);
      el = $4(sel, document);
    }
    return el || null;
  }

  _calcTargetSizeFromPsiRect(psiRect) {
    const w0 = Math.max(1, Math.round(psiRect.width));
    const h0 = Math.max(1, Math.round(psiRect.height));
    const viewportW = Math.max(1, document.documentElement?.clientWidth || window.innerWidth || 1);
    const vwLimit = Math.max(1, viewportW - 30);
    const maxW = Math.min(580, vwLimit);
    const targetW = Math.min(Math.round(w0 * 2), Math.round(maxW));
    const k = targetW / w0;
    const targetH = Math.max(1, Math.round(h0 * k));
    return { targetW, targetH };
  }

  _applyDialogFixedSize(targetW, targetH) {
    const dlg = this.dialog;
    const prev = { w: dlg.style.width, h: dlg.style.height, opacity: dlg.style.opacity, visibility: dlg.style.visibility };
    dlg.style.width = `${targetW}px`;
    dlg.style.height = `${targetH}px`;
    return prev;
  }

  _resolveVarPx(varName, fallback = 12) {
    if (this._radiusCache.has(varName)) return this._radiusCache.get(varName);
    const tmp = document.createElement('div');
    tmp.style.position = 'absolute';
    tmp.style.visibility = 'hidden';
    tmp.style.borderTopLeftRadius = `var(${varName})`;
    document.body.appendChild(tmp);
    const px = parseFloat(getComputedStyle(tmp).borderTopLeftRadius) || fallback;
    tmp.remove();
    this._radiusCache.set(varName, px);
    return px;
  }

  _getOrCreateGhost() {
    if (!this._ghost) {
      const ghost = document.createElement('div');
      ghost.className = 'hdt-story-ghost';
      // Dialog + ::backdrop live in the top layer, z-index can't beat it: promote the ghost
      // to the top layer too (popover opened after the dialog) so the backdrop doesn't dim/blur it
      ghost.setAttribute('popover', 'manual');
      Object.assign(ghost.style, {
        position: 'fixed',
        inset: 'auto',
        margin: '0',
        padding: '0',
        border: '0',
        background: 'transparent',
        transformOrigin: 'top left',
        pointerEvents: 'none',
        zIndex: '11',
        overflow: 'hidden',
        contain: 'strict'
      });
      document.body.appendChild(ghost);
      try { ghost.showPopover?.(); } catch {}
      this._ghost = ghost;
    }
    return this._ghost;
  }

  _resetGhost(ghost, startRect, radiusPx, psi) {
    Object.assign(ghost.style, {
      left: `${startRect.left}px`,
      top: `${startRect.top}px`,
      width: `${startRect.width}px`,
      height: `${startRect.height}px`,
      borderRadius: `${radiusPx}px`,
      opacity: '1',
      transform: 'none'
    });

    ghost.replaceChildren();
    const img = psi?.querySelector?.('img');
    const video = psi?.querySelector?.('video');
    const svg = !img && !video && psi?.querySelector?.('.hdt-psi-wrap svg');
    // Video card: snapshot the current frame so the ghost matches what the user clicked
    if (video && video.readyState >= 2 && video.videoWidth) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      try {
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        Object.assign(canvas.style, {
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: getComputedStyle(video).objectFit || 'cover'
        });
        ghost.appendChild(canvas);
        return;
      } catch {}
    }
    if (img) {
      // Reuse the already decoded source only: no srcset/sizes => no new request/decode mid-animation
      const gImg = document.createElement('img');
      gImg.src = img.currentSrc || img.src;
      gImg.alt = '';
      gImg.decoding = 'sync';
      const cs = getComputedStyle(img);
      Object.assign(gImg.style, {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: cs.objectFit || 'cover',
        objectPosition: cs.objectPosition || 'center'
      });
      ghost.appendChild(gImg);
    } else if (svg) {
      // No image picked: placeholder SVG (its preserveAspectRatio crops like object-fit: cover)
      // Own ids: a url(#id) reference resolves to the first match, which may be hidden
      const gSvg = new DOMParser().parseFromString(svg.outerHTML.replace(/(id="|url\(#)/g, '$1g-'), 'image/svg+xml').documentElement;
      gSvg.removeAttribute('class');
      Object.assign(gSvg.style, { width: '100%', height: '100%', display: 'block' });
      ghost.appendChild(gSvg);
    } else {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      div.style.background = psi ? getComputedStyle(psi).backgroundColor || '#000' : '#000';
      ghost.appendChild(div);
    }
  }

  _destroyGhost() {
    if (this._ghost) {
      const img = $4('img', this._ghost);
      if (img) { try { img.src = ''; } catch(e) {} }
      this._ghost.replaceChildren();
      this._ghost.remove();
      this._ghost = null;
    }
  }

  _calcTransform(startRect, endRect) {
    const dx = endRect.left - startRect.left;
    const dy = endRect.top  - startRect.top;
    const sx = Math.max(0.01, endRect.width  / Math.max(1, startRect.width));
    const sy = Math.max(0.01, endRect.height / Math.max(1, startRect.height));
    return { dx, dy, sx, sy };
  }

  // Transform mapping a box laid out at `boxRect` onto `targetRect` (transform-origin: top left)
  _rectTransform(boxRect, targetRect) {
    const { dx, dy, sx, sy } = this._calcTransform(boxRect, targetRect);
    return `translate3d(${dx}px, ${dy}px, 0px) scale(${sx}, ${sy})`;
  }

  // Transform-only (WAAPI, compositor): keeps running smoothly while the main thread is busy opening the dialog.
  // The ghost is laid out at the dialog size, so it is pixel-sharp at scale(1) where it hands off to the modal video.
  // Default: spring (close / interrupted open). `options` overrides it, e.g. a fixed-duration tween for the open.
  _animateGhost(ghost, from, to, visualDuration = 0.6, options = null) {
    return animate(ghost, { transform: [from, to] }, options || { type: 'spring', visualDuration, bounce: 0 });
  }

  // Close requested while still opening: fly the ghost back to the card from wherever it is now
  _reverseOpen() {
    const state = this._openState, ghost = this._ghost, dlg = this.dialog;
    state.cancelled = true;
    const current = { transform: getComputedStyle(ghost).transform, opacity: getComputedStyle(ghost).opacity, dlgOpacity: getComputedStyle(dlg).opacity };
    state.ghostControls?.stop();
    state.fade?.stop();
    this._phase = 'closing';
    const psi = this._openPsiEl?.isConnected ? this._openPsiEl : null;
    const cardRect = this._compensateRectForMarquee(psi ? this._rect(psi) : this._openPsiRectSnapshot);
    const boxRect = { left: parseFloat(ghost.style.left), top: parseFloat(ghost.style.top), width: parseFloat(ghost.style.width), height: parseFloat(ghost.style.height) };
    ghost.style.transform = current.transform;
    animate(ghost, { opacity: [current.opacity, 1] }, { duration: 0.12, ease: 'easeOut' });
    animate(dlg, { opacity: [current.dlgOpacity, 0] }, { duration: 0.12, ease: 'easeOut' });
    const ctrl = this._animateGhost(ghost, current.transform, this._rectTransform(boxRect, cardRect), 0.45);
    ctrl.finished.catch(() => {}).finally(() => {
      // Modal.open() is still awaiting the stopped open animation and will never remove its class: do it here.
      // (Its awaited promise is left pending on purpose so it can't dispatch dialogOpen / trap focus on a closed dialog.)
      dlg.classList.remove('dialog-opening');
      this._afterClose(psi, this._openToken);
    });
    return ctrl;
  }

  _afterClose(psi, tokenAtClose) {
    this._toggleSourceCard(psi || this._openPsiEl, false);
    this._destroyGhost();
    this._openState = null;
    this._openTriggerEl = null;
    this._openPsiEl = null;
    this._openPsiRectSnapshot = null;
    this._marqueeHold = false;
    this._disableMarqueeHoldGuards();
    if (!(this._productsStorySlider?.isConnected)) this._productsStorySlider = null;
    const shouldResume = this._getStopMarqueeFlag() && !!this._marqueeAutoConfigured;
    if (shouldResume && tokenAtClose === this._openToken) this._playMarquee();
    this._unlock();
  }

  /**
   * Get the modal video of the opened story ready to take over from the ghost:
   * lazy source loaded, seeked to the card's current time and that frame painted (kept paused).
   * @returns {Promise<HTMLVideoElement|undefined>}
   */
  async _prepareModalVideo(trigger, psi) {
    const cardVideo = $4('video', psi),
    story = $4('.hdt-slider--story', this),
    slide = story && $4(`.hdt-product-story-item[data-block_index="${trigger?.dataset.index}"]`, story),
    video = slide && $4('hdt-video video', slide);
    if (!video) return;
    const once = (type) => new Promise((resolve) => video.addEventListener(type, resolve, { once: true }));

    // hdt-el-lazy loads the source (and calls load(), resetting time) once the slide is visible
    if (video.querySelector('source[data-src]')) await once('lazy:loaded');
    if (video.readyState < 1) await once('loadedmetadata');
    video.pause();
    if (cardVideo && Math.abs(video.currentTime - cardVideo.currentTime) > 0.04) {
      const painted = video.requestVideoFrameCallback
        ? new Promise((resolve) => video.requestVideoFrameCallback(() => resolve()))
        : once('seeked').then(() => new Promise((resolve) => requestAnimationFrame(resolve)));
      video.currentTime = cardVideo.currentTime;
      await painted;
    } else if (video.readyState < 2) {
      await once('loadeddata');
    }
    return video;
  }

  /**
   * The user may have moved to another story in the modal: close onto that story's card, not the one clicked to open.
   * If that card is clipped by the slider viewport, jump the slider to it first (hidden behind the modal).
   * @returns {boolean} true when the slider jumped
   */
  _retargetCloseCard() {
    const storyApi = $4('.hdt-slider--story', this)?.apiS,
    slider = this._productsStorySlider || this.previousElementSibling,
    index = storyApi?.slideNodes()[storyApi.selectedScrollSnap()]?.dataset.block_index,
    trigger = index != null && $4(`hdt-control-modal-story[data-index="${index}"]`, slider);
    if (!trigger || trigger === this._openTriggerEl) return false;

    const viewport = $4('.hdt-slider__viewport', slider) || slider,
    cardRect = this._rect(trigger),
    vpRect = this._rect(viewport);
    const jump = !!slider.apiS && (cardRect.left < vpRect.left - 1 || cardRect.right > vpRect.right + 1);
    if (jump) slider.apiS.scrollTo(parseInt(index), true);

    this._toggleSourceCard(this._openPsiEl, false);
    this._openTriggerEl = trigger;
    this._openPsiEl = this._getPsiInner(trigger);
    this._openPsiRectSnapshot = null;
    this._toggleSourceCard(this._openPsiEl, true);
    return jump;
  }

  // Hide the source card while its ghost is "outside" it, so there is only one copy on screen
  _toggleSourceCard(psi, hidden) {
    if (psi) psi.style.visibility = hidden ? 'hidden' : '';
  }

  _ensureSliderFromTrigger(trigger) {
    if (this._productsStorySlider && this._productsStorySlider.isConnected) return this._productsStorySlider;
    this._productsStorySlider = trigger?.closest?.('.hdt-slider--products-story') || null;

    if (this._productsStorySlider && this._marqueeAutoConfigured === null) {
      this._snapshotMarqueeAutoConfigured();
    }
    return this._productsStorySlider;
  }

  _getSliderApi() {
    const el = this._productsStorySlider;
    if (!el) return null;
    return el.apiS || el.api || el.slider || null;
  }

  _getAutoScrollPlugin() {
    const api = this._getSliderApi();
    if (!api) return null;
    try {
      const plugs = typeof api.plugins === 'function' ? api.plugins() : api.plugins;
      return plugs?.autoScroll || null;
    } catch {
      return null;
    }
  }

  _snapshotMarqueeAutoConfigured() {
    let configured = !!this._getAutoScrollPlugin();
    if (!configured) {
      const cfg = this._getSliderConfig();
      if (cfg && Object.prototype.hasOwnProperty.call(cfg, 'marquee')) {
        configured = Number(cfg.marquee) !== 0;
      }
    }
    this._marqueeAutoConfigured = !!configured;
  }

  async _stopMarquee(delayMs = 1) {
    if (!this._productsStorySlider) return false;
    if (delayMs > 0) await this.wait(delayMs);
    try {
      this.#autoScroll.stop();
      return true;
    } catch {
      return false;
    }
  }

  _playMarquee() {
    if (!this._productsStorySlider) return false;
    try {
      this.#autoScroll.play();
      return true;
    } catch {
      return false;
    }
  }

  _getSliderConfig() {
    const el = this._productsStorySlider;
    if (!el) return null;
    let cfg = null;
    try { cfg = el.config && typeof el.config === 'object' ? el.config : null; } catch {}
    if (!cfg) {
      const attr = el.getAttribute && el.getAttribute('config');
      if (attr) { try { cfg = JSON.parse(attr); } catch {} }
    }
    return cfg && typeof cfg === 'object' ? cfg : null;
  }

  _shouldApplyMarqueeCompensation() {
    const vp = Math.max(1, document.documentElement?.clientWidth || window.innerWidth || 1);
    if (vp < 1025) return false;
    const cfg = this._getSliderConfig();
    if (!cfg || !Object.prototype.hasOwnProperty.call(cfg, 'marquee')) return false;
    const m = Number(cfg.marquee) || 0;
    if (m === 0) return false;
    const stopFlag = this._getStopMarqueeFlag();
    if (stopFlag) return false;
    return true;
  }

  _compensateRectForMarquee(rect) {
    if (!this._shouldApplyMarqueeCompensation()) return rect;
    const cfg = this._getSliderConfig();
    const m = Number(cfg.marquee) || 0;
    const dir = m > 0 ? -1 : 1;
    const comp = Number(this._marqueeCompensationPx || 0);
    if (!comp) return rect;
    const left = rect.left + dir * comp;
    return { left, top: rect.top, width: rect.width, height: rect.height };
  }

  _enableMarqueeHoldGuards() {
    if (!this._productsStorySlider || this._marqueeGuardAbort) return;
    this._marqueeGuardAbort = new AbortController();
    const { signal } = this._marqueeGuardAbort;
    const reStop = () => { if (this._marqueeHold) { this._stopMarquee(0); } };
    const slider = this._productsStorySlider;
    slider.addEventListener('pointerenter', reStop, { signal, passive: true, capture: true });
    slider.addEventListener('pointerleave', reStop, { signal, passive: true, capture: true });
    slider.addEventListener('pointerup',    reStop, { signal, passive: true, capture: true });
    window.addEventListener('mouseup',      reStop, { signal, passive: true, capture: true });
    window.addEventListener('touchend',     reStop, { signal, passive: true, capture: true });
    setTimeout(() => reStop(), 0);
    setTimeout(() => reStop(), 60);
  }

  _disableMarqueeHoldGuards() {
    if (this._marqueeGuardAbort) {
      try { this._marqueeGuardAbort.abort(); } catch {}
      this._marqueeGuardAbort = null;
    }
  }
  async animateDialogOpen() {
    if (!this._lock('opening')) return animate(document.body, {}, { duration: 0 });
    const dlg = this.dialog;
    if (!dlg) { this._unlock(); return animate(document.body, {}, { duration: 0 }); }
    const token = ++this._openSeq;
    this._openToken = token;
    const prevVis = '';
    const prevOpacity = ''; // never restore a stale inline value: the close fade leaves opacity: 0 behind
    dlg.style.visibility = 'hidden';
    dlg.style.opacity = '0';
    dlg.style.transform = '';
    // The card that opened the modal: the opening button is inside it (a stale .hdt-just-clicked could point to an older card)
    const trigger = this.dialog.btnOpening?.closest?.('hdt-control-modal-story') || await this._waitForJustClicked(5, 16);
    if (!trigger) {
      dlg.style.visibility = prevVis || '';
      const ctrl = animate(
        dlg,
        { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0)'] },
        { duration: 0.3, ease: [0.19, 1, 0.22, 1] }
      );
      ctrl.finished.finally(() => { dlg.style.opacity = prevOpacity || ''; this._unlock(); });
      return ctrl;
    }

    const psi = this._getPsiInner(trigger);
    if (!psi) {
      dlg.style.visibility = prevVis || '';
      const ctrl = animate(
        dlg,
        { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0)'] },
        { duration: 0.3, ease: [0.19, 1, 0.22, 1] }
      );
      ctrl.finished.finally(() => { dlg.style.opacity = prevOpacity || ''; this._unlock(); });
      return ctrl;
    }
    this._openTriggerEl = trigger;
    this._openPsiEl = psi;
    this._ensureSliderFromTrigger(trigger);
    const stopFlag = this._getStopMarqueeFlag();
    if (stopFlag && this._marqueeAutoConfigured) {
      this._marqueeHold = true;
      await this._stopMarquee(0);
      this._enableMarqueeHoldGuards();
    }
    const startRect = this._rect(psi);
    this._openPsiRectSnapshot = { left: startRect.left, top: startRect.top, width: startRect.width, height: startRect.height };
    const { targetW, targetH } = this._calcTargetSizeFromPsiRect(startRect);
    const prevDlg = this._applyDialogFixedSize(targetW, targetH);
    dlg.getBoundingClientRect();
    const endRect = this._rect(dlg);
    const rStart = this._resolveVarPx('--rounded-product-card', 12);
    const rEnd   = this._resolveVarPx('--rounded', 16);
    const ghost = this._getOrCreateGhost();
    // Ghost laid out at the dialog rect, shrunk onto the card: it lands at scale(1) => sharp, same pixels as the modal
    this._resetGhost(ghost, endRect, rEnd, psi);
    const fromCard = this._rectTransform(endRect, startRect);
    ghost.style.transform = fromCard;
    this._toggleSourceCard(psi, true);
    dlg.style.visibility = prevVis || 'visible';
    const state = this._openState = { cancelled: false };
    // Fixed-duration tween: a spring's settling tail (~0.95s for visualDuration 0.6) delayed the video hand-off
    const ghostControls = state.ghostControls = this._animateGhost(ghost, fromCard, 'translate3d(0px, 0px, 0px) scale(1, 1)', 0, { duration: 0.45, ease: [0.32, 0.72, 0, 1] });
    const modalMediaReady = this._prepareModalVideo(trigger, psi);
    const check = (value) => { if (state.cancelled) throw new Error('cancelled'); return value; };
    ghostControls.finished
      .then(() => {
        check();
        // The ghost now covers the dialog exactly: show the dialog underneath at once (no double frame)
        dlg.style.opacity = '1';
        // Hand-off: wait until the modal video shows the same frame (max 1s), then dissolve the ghost
        return Promise.race([modalMediaReady, this.wait(1000)]);
      })
      .then((video) => {
        check();
        // Start the modal video exactly when the dissolve starts: same frame under the ghost
        const btnSound = $4('hdt-sound-video[current]', this);
        if (video) this.#playSafe(video, () => btnSound ? btnSound.setMuted(true) : (video.muted = true));
        state.fade = animate(ghost, { opacity: [1, 0] }, { duration: 0.2, ease: 'easeInOut' });
        return state.fade.finished;
      })
      .then(check)
      .then(() => {
        if (this._marqueeHold) { this._stopMarquee(0); }
        this._destroyGhost();
        this._openState = null;
        dlg.style.opacity = prevOpacity || '';
        this._unlock();
      }, () => {});
    return ghostControls;
  }

  async animateDialogClose() {
    if (this._phase === 'opening' && this._openState && this._ghost) return this._reverseOpen();
    if (!this._lock('closing')) return animate(document.body, {}, { duration: 0 });
    const dlg = this.dialog;
    if (!dlg) { this._unlock(); return animate(document.body, {}, { duration: 0 }); }
    const tokenAtClose = this._openToken;
    // Embla applies a jump on its next frame: wait for it before measuring the card
    if (this._retargetCloseCard()) await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const dlgRect = this._rect(dlg);
    let psi = (this._openPsiEl && this._openPsiEl.isConnected) ? this._openPsiEl : null;
    let endRect;
    if (psi) {
      endRect = this._rect(psi);
    } else if (this._openPsiRectSnapshot) {
      endRect = this._openPsiRectSnapshot;
    } else {
      const ctrl = animate(
        dlg,
        { opacity: [1, 0], transform: ['translateY(0)', 'translateY(12px)'] },
        { duration: 0.25, ease: [0.19, 1, 0.22, 1] }
      );
      ctrl.finished.finally(() => {
        this._marqueeHold = false;
        this._disableMarqueeHoldGuards();

        const stopFlag = this._getStopMarqueeFlag();
        const shouldResume = stopFlag && !!this._marqueeAutoConfigured;
        if (shouldResume && tokenAtClose === this._openToken) {
          this._playMarquee();
        }
        this._unlock();
      });
      return ctrl;
    }
    endRect = this._compensateRectForMarquee(endRect);
    const rStart = this._resolveVarPx('--rounded', 16);
    const ghost = this._getOrCreateGhost();
    // Ghost = what the modal shows now; the (hidden) card video jumps to the same time so the landing matches too
    const modalPsi = $4('.hdt-slider--story .hdt-product-story-item.is-selected .hdt-psi-inner', this),
    modalVideo = $4('video', modalPsi),
    cardVideo = $4('video', psi);
    if (modalVideo && cardVideo) cardVideo.currentTime = modalVideo.currentTime;
    this._resetGhost(ghost, dlgRect, rStart, modalPsi || psi);
    // Ghost starts transparent above the dialog and fades in while the dialog fades out
    const fadeInGhost = animate(ghost, { opacity: [0, 1] }, { duration: 0.15, ease: 'easeOut' });
    const fadeOutDlg = animate(dlg, { opacity: [1, 0] }, { duration: 0.2, ease: 'easeOut' });
    const ghostControls = this._animateGhost(ghost, 'translate3d(0px, 0px, 0px) scale(1, 1)', this._rectTransform(dlgRect, endRect), 0.5);
    Promise.all([ghostControls.finished, fadeOutDlg.finished, fadeInGhost.finished])
      .catch(() => {})
      .finally(() => this._afterClose(psi, tokenAtClose));

    return ghostControls;
  }
}

class ControlModalStory extends HTMLElement {
    #controller;

    connectedCallback() {
        // One click listener per card (no observer per card: the auto scrolling row keeps cards entering / leaving)
        this.#controller = new AbortController();
        this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
    }

    disconnectedCallback() {
        this.#controller?.abort();
        this.classList.remove('hdt-just-clicked');
    }

    #onClick = (e) => {
        if (!this.contains(e.target) || !this.dataset.slider) return;
        // Fallback for the modal open animation (it reads the opening button first)
        const parentSlider = this.closest('.hdt-slider--products-story');
        $$4('.hdt-product-story-item.hdt-just-clicked', parentSlider).forEach(el => {
            if (el !== this) el.classList.remove('hdt-just-clicked');
        });
        this.classList.add('hdt-just-clicked');
    }
}

/**
 * Sound toggle button. Videos are muted by default and only one video plays sound at a time:
 * unmuting dispatches ThemeEvents.videoUnmute, every other button then mutes its videos.
 * data-scope: closest selector holding the controlled video(s)
 * [current]: label uses the selected slide video (story modal)
 */
class SoundVideo extends HTMLElement {
  #controller;
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.scope = this.closest(this.dataset.scope) || this.parentElement;
    this.button = $4('button', this);
    this.update(this.hasAttribute('muted'));

    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.setMuted(!this.hasAttribute('muted'));
    }, { signal });

    // Keep state in sync when the video is muted elsewhere (autoplay fallback...)
    this.scope.addEventListener('volumechange', (e) => {
      if (e.target === this.video) this.update(e.target.muted);
    }, { capture: true, signal });

    document.addEventListener(videoUnmute, (e) => {
      if (e.detail?.source !== this && !this.hasAttribute('muted')) this.setMuted(true);
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get videos() {
    return $$4('video', this.scope);
  }

  get video() {
    return this.hasAttribute('current') ? $4('.is-selected video', this.scope) : this.videos[0];
  }

  setMuted(muted) {
    this.videos.forEach((video) => video.muted = muted);
    this.update(muted);
    if (!muted) document.dispatchEvent(new CustomEvent(videoUnmute, { detail: { source: this } }));
  }

  update(muted) {
    this.toggleAttribute('muted', muted);
    const label = this.button.dataset[muted ? 'unmute' : 'mute'],
    des = this.video?.getAttribute('aria-label')?.trim();
    // No description: drop the ": [description]" part
    if (label) this.button.setAttribute('aria-label', des ? label.replace('[description]', des) : label.replace(/\s*[:：]?\s*\[description\]/, ''));
  }
}

registerEl('hdt-sound-video', SoundVideo);
registerEl('hdt-modal-story', ModalStory);
registerEl('hdt-control-modal-story', ControlModalStory);

class HdtShrinkOnSroll extends HTMLElement {
  constructor() {
    super();
    this.inner = null;
    this.cleanupFns = [];
    this._translateEls = [];
    this._onResize = this._onResize.bind(this);
    // The setup only depends on the mobile breakpoint: scroll() already tracks size changes itself.
    // Avoids rebuilding every animation on each resize (mobile URL bar shows/hides while scrolling)
    this._mobileQuery = window.matchMedia('(max-width: 767.98px)');
  }

  connectedCallback() {
    if (!matchMediaQuery("motion")) return;
    this.inner = $4('.hdt-shrink-on-sroll-inner', this);
    this._mobileQuery.addEventListener('change', this._onResize);
    this._setupScrollEffect();
  }

  disconnectedCallback() {
    if (!matchMediaQuery("motion")) return;
    this._cleanup();
    this._mobileQuery.removeEventListener('change', this._onResize);
  }

  _cleanup() {
    this.cleanupFns.forEach(fn => fn && fn());
    this.cleanupFns = [];
  }

  _isMobile() {
    return this._mobileQuery.matches;
  }

  _setupScrollEffect() {
    this._cleanup();
    const isExpand = this.dataset.expand === 'true';
    const expandFromBottom = this.dataset.expandFromBottom === 'true';
    const removeRoundOnExpand = this.dataset.expandRemoveRound !== 'false';
    if (this.getAttribute('mobile') === 'false' && this._isMobile()) {
      // Clear after Motion has flushed the stopped animations, otherwise their last values stay inline
      frame.postRender(() => {
        if (this.inner) this.inner.style.clipPath = '';
        $$4('[data-shrink-translate], [data-shrink-translate_mb]', this).forEach(el => el.style.transform = '');
      });
      return;
    }
    if (isExpand && expandFromBottom) {
      this._setupBottomExpandEffect(removeRoundOnExpand);
      return;
    }
    if (this.inner) {
      const fromClip = isExpand ? `inset(0 var(--clip-x) 0 var(--clip-x) round var(--rounded-lg))` : `inset(0 round 0)`;
      const toClip = isExpand ? (removeRoundOnExpand ? `inset(0 round 0)` : `inset(0 round var(--rounded-lg))`) : `inset(0 var(--clip-x) 0 var(--clip-x) round var(--rounded-lg))`;
      const offsets = isExpand ? ['0 1', '1 0.7'] : ['1 0.8', '1 0.1'];
      this.cleanupFns.push(
        scroll( animate(this.inner, { clipPath: [fromClip, toClip] }), { offset: offsets, target: this } )
      );
    }
    this._setupTranslateEffects(isExpand);
  }

  _setupBottomExpandEffect(removeRoundOnExpand = true) {
    const el = this;
    const inner = this.inner;
    if (!inner) return;
    const START_GAP = 60;
    const END_GAP = 80;
    // Translated children for the current breakpoint, queried once instead of on every scroll frame
    const attrName = this._isMobile() ? 'data-shrink-translate_mb' : 'data-shrink-translate';
    this._translateEls = $$4(`[${attrName}]`, this).map(el => {
      let dir = (el.getAttribute(attrName) || '').trim();
      // RTL: start is on the right, same as _setupTranslateEffects
      if (isRTL && dir) dir = dir == 'start' ? 'end' : 'start';
      return dir ? { el, base: dir === 'start' ? 'var(--clip-x)' : dir === 'end' ? 'calc(var(--clip-x) * -1)' : '0px' } : null;
    }).filter(Boolean);
    let lastProgress;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const bottom = rect.bottom;
      let progress;
      const start = vh - START_GAP;
      const end = END_GAP;
      if (bottom >= start) {
        progress = 0;
      } else if (bottom <= end) {
        progress = 1;
      } else {
        progress = (start - bottom) / (start - end);
      }
      // Same value as the last frame (before or after the effect range): nothing to write
      progress = Math.round(progress * 1000) / 1000;
      if (progress === lastProgress) return;
      lastProgress = progress;
      this._applyExpandProgress(progress, removeRoundOnExpand);
      this._applyTranslateProgress(progress);
    };

    // Initial state, then follow the scroll only while the section is on screen: the progress only changes
    // while its bottom edge is in view, so no layout read or style write on every scroll frame of the page
    onScroll();
    let stopScroll;
    // 200px early: a jump (Home key, back to top) skips the leave callback, so correct the state before it shows
    const stopInView = inView(this, () => {
      onScroll();
      stopScroll = scroll(onScroll);
      // Left the screen: settle on the final value (fast scroll may skip the last frames)
      return () => {
        stopScroll?.();
        stopScroll = null;
        onScroll();
      };
    }, { margin: '200px 0px 200px 0px' });
    this.cleanupFns.push(() => {
      stopInView();
      stopScroll?.();
    });
  }

  _applyExpandProgress(progress, removeRoundOnExpand = true) {
    const p = Math.min(Math.max(progress, 0), 1);
    if (removeRoundOnExpand) {
      this.inner.style.clipPath = `inset(0 calc(var(--clip-x) * ${1 - p}) 0 calc(var(--clip-x) * ${1 - p}) round calc(var(--rounded-lg) * ${1 - p}))`;
    } else {
      this.inner.style.clipPath = `inset(0 calc(var(--clip-x) * ${1 - p}) 0 calc(var(--clip-x) * ${1 - p}) round var(--rounded-lg))`;
    }
  }

  _applyTranslateProgress(progress) {
    const p = Math.min(Math.max(progress, 0), 1);
    this._translateEls.forEach(({ el, base }) => {
      el.style.transform = `translateX(calc(${base} * ${1 - p}))`;
    });
  }

  _setupTranslateEffects(isExpand = false) {
    const isMobile = this._isMobile();
    const candidates = $$4('[data-shrink-translate], [data-shrink-translate_mb]', this);
    candidates.forEach(el => {
      const attrName = isMobile ? 'data-shrink-translate_mb' : 'data-shrink-translate';
      if (!el.hasAttribute(attrName)) return;
      let dir = (el.getAttribute(attrName) || '').trim(); // "start" | "end"
      if (!dir) return;
      dir = isRTL ? dir == 'start' ? 'end': 'start': dir;
      let fromVal = dir === 'start' ? 'calc(var(--clip-x) * 1)' : dir === 'end' ? 'calc(var(--clip-x) * -1)' : '0px';
      let toVal = '0px';
      if (!isExpand) {
        fromVal = '0px';
        toVal = dir === 'start' ? 'calc(var(--clip-x) * 1)' : dir === 'end' ? 'calc(var(--clip-x) * -1)' : '0px';
      }
      const offsets = isExpand ? ['0 1', '1 0.7'] : ['1 0.8', '1 0.1'];
      const stop = scroll(
        animate(el, { transform: [`translateX(${fromVal})`, `translateX(${toVal})`] }),
        { offset: offsets, target: this }
      );
      this.cleanupFns.push(stop);
    });
  }

  _onResize() {
    this._setupScrollEffect();
  }
}

registerEl('hdt-shrink-on-sroll', HdtShrinkOnSroll);

class btnAutoScroll extends HTMLElement {
  #btn;
  #wPlay;
  #wPause;
  #auto;
  // Paused by the visitor (or reduced motion): the plugin restarts on swipe, mouseleave and focusout, keep it stopped
  #userStopped = !matchMediaQuery('motion');
  #controller;
  #api;
  #handlers = {};
  connectedCallback() {
    this.#btn    = $4('.hdt-btn-control_media', this);
    this.#wPlay  = $4('.hdt-ic-play-wrap', this)
    this.#wPause = $4('.hdt-ic-pause-wrap', this);

    const slider = this.#btn && $id4(`${this.#btn.getAttribute('aria-controls')}`);
    if (!slider) return this.toggleAttribute('hidden', true);
    this.toggleAttribute('hidden', !slider.apiS || slider.apiS.scrollSnapList().length < 2 || !this.#active(slider));
    if(!slider.apiS) return;
    this.#api = slider.apiS;
    this.#auto = slider.apiS.plugins()?.autoScroll;
    // Removed on disconnect: connected again (theme editor), a second click handler would stop then play right away
    this.#controller = new AbortController();
    this.#btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.#userStopped = this.#isPlaying;
      if (this.#userStopped) {
        this.#auto.stop();
      } else {
        this.#auto.play();
      }
    }, { signal: this.#controller.signal });
    this.#handlers = {
      'autoScroll:play': ()=> {
        // The plugin emits before flagging itself as playing: stop it once the play has started
        if (this.#userStopped) return queueMicrotask(() => this.#auto.stop());
        this.#sync(true);
      },
      'autoScroll:stop': ()=> this.#sync(false),
      'reInit': (apiS)=> {
        this.toggleAttribute('hidden', apiS.scrollSnapList().length < 2 || !this.#active(slider));
      }
    };
    Object.entries(this.#handlers).forEach(([event, handler]) => this.#api.on(event, handler));
    if (this.#userStopped && this.#isPlaying) this.#auto.stop();
    this.#sync();
  }
  disconnectedCallback() {
    this.#controller?.abort();
    Object.entries(this.#handlers).forEach(([event, handler]) => this.#api?.off(event, handler));
    this.#handlers = {};
    this.#api = null;
  }
  #active(slider) {
    return slider.autoScrollActive && slider.autoScrollActive();
  }

  get #isPlaying() { return this.#auto?.isPlaying(); }

  #sync(playing = null) {
    if (playing == null) playing = this.#isPlaying;
    //console.log('playing: ', playing)
    this.#wPlay.toggleAttribute('hidden',  playing);
    this.#wPause.toggleAttribute('hidden', !playing);
    // Icon only button: a fixed label with a pressed state. Text button: the shown text is its name
    if (this.#btn.hasAttribute('aria-label')) this.#btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
  }
}
registerEl('hdt-pause-play-slider', btnAutoScroll);

/**
 * Tell whether the coming focus comes from a pointer (mouse, touch, pen) press.
 * Focus helpers that scroll the page skip it: a scroll on press moves the target away before the click.
 * Keyboard and screen reader focus (no pointer press) still scroll.
 * @returns {() => boolean} returns true once for a pointer focus
 */
function pointerFocusTracker(el, signal) {
  let pointer = false, timer;
  const release = () => {
    clearTimeout(timer);
    // Touch: the focus follows the release (emulated mousedown)
    timer = setTimeout(() => { pointer = false; }, 1000);
  };
  el.addEventListener('pointerdown', () => { clearTimeout(timer); pointer = true; }, { capture: true, signal });
  el.addEventListener('pointerup', release, { capture: true, signal });
  el.addEventListener('pointercancel', release, { capture: true, signal });
  el.addEventListener('keydown', () => { pointer = false; }, { capture: true, signal });
  return () => {
    const fromPointer = pointer;
    pointer = false;
    return fromPointer;
  };
}

class HeroCollections extends HTMLElement {
  #scrollEl;
  #isPointerFocus = () => false;
  #inner;
  #content;
  #headerWrap;
  #header;
  #bg;
  #cards;
  #cleanupFns = [];
  #controller;
  // Listeners that live as long as the element is connected
  #connectedController;
  #stopResize;
  #focusToken = 0;
  #focusTimer = null;
  // Fraction of the pinned scroll range where cards stay still (start / end)
  #hold = 0;
  #holdEnd = 0;

  connectedCallback() {
    this.#scrollEl = $4('.hdt-collections-tilted-scroll', this);
    this.#inner = $4('.hdt-collections-tilted-scroll-inner', this);
    if (!this.#scrollEl || !this.#inner) return;
    this.#content = $4('.hdt-hero-collections-content', this);
    this.#headerWrap = $4('.hdt-section__header-wrap', this);
    this.#header = $4('.hdt-section__header', this);
    this.#bg = $4('.hdt-hero-bg-animate', this);
    this.#cards = $$4('.hdt-cl-tilted-card', this);

    // Measure in the read phase instead of forcing layout on connect
    frame.read(() => {
      if (this.isConnected) this.#init();
    });
    const rebuild = () => {
      if (!this.isConnected) return;
      this.#cleanup();
      // Reset after Motion has flushed the stopped animations, otherwise their last values stay inline
      frame.postRender(() => {
        this.#resetStyles();
        this.#init();
      });
    };
    // Debounced: re-measuring on every resize frame is expensive
    this.#stopResize = resize(debounce(rebuild, 150));

    this.#connectedController = new AbortController();
    const { signal } = this.#connectedController;
    // Reduced motion toggled while the page is open: rebuild with or without decorative effects
    window.matchMedia('(prefers-reduced-motion: no-preference)').addEventListener('change', rebuild, { signal });

    // Theme editor: bring the selected collection card into view
    if (window.Shopify?.designMode) {
      document.addEventListener(admEvts.select, (e) => {
        if (!this.contains(e.target)) return;
        const card = $4('.hdt-cl-tilted-card', e.target);
        if (!card) return;
        this.#cancelFocusScroll();
        if (this.#scrollEl.classList.contains('hdt-has-cards')) {
          // Scroll animation active: cards move with the page scroll
          this.#scrollCardIntoView(card, 0, this.#focusToken, false);
        } else {
          // Swipe layout: scroll the card row
          e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.#connectedController?.abort();
    this.#stopResize?.();
    this.#cleanup();
    this.#resetStyles();
  }

  #cleanup() {
    this.#controller?.abort();
    this.#cancelFocusScroll();
    this.#cleanupFns.forEach(fn => fn?.());
    this.#cleanupFns = [];
  }

  #cancelFocusScroll() {
    this.#focusToken++;
    clearTimeout(this.#focusTimer);
    this.#focusTimer = null;
  }

  #clamp(v, min = 0, max = 1) {
    return Math.min(max, Math.max(min, v));
  }

  get #isDesktop() {
    return matchMediaQuery('hover') && window.innerWidth > 1024;
  }

  #resetStyles() {
    this.style.removeProperty('--content-height');
    this.#bg?.style.removeProperty('clip-path');
    this.#content?.style.removeProperty('clip-path');
    this.#content?.style.removeProperty('transform');
    this.#headerWrap?.style.removeProperty('transform');
    this.#header?.style.removeProperty('opacity');
    this.#header?.style.removeProperty('transform');
    this.#inner.style.removeProperty('transform');
    this.#scrollEl.style.removeProperty('--opacity-progress');
    this.#scrollEl.classList.remove('hdt-has-cards');
    this.#cards.forEach(card => card.style.removeProperty('--angle-percent'));
  }

  #getScrollBounds() {
    const rect = this.getBoundingClientRect(),
    startY = window.scrollY + rect.top,
    endY = window.scrollY + rect.bottom - window.innerHeight;
    return [startY, Math.max(1, endY - startY)];
  }

  // needFocus: keyboard focus must still be inside the card (false for theme editor block select)
  #scrollCardIntoView(card, attempt, token, needFocus = true) {
    if (token !== this.#focusToken || !card || (needFocus && !card.contains(document.activeElement))) return;
    const vwRect = this.#scrollEl.getBoundingClientRect(),
    cr = card.getBoundingClientRect(),
    overscan = parseFloat(this.dataset.focusOverscan) || 24,
    leftInView = cr.left - vwRect.left,
    rightInView = cr.right - vwRect.left,
    leftThreshold = isRTL ? overscan : 0,
    rightThreshold = isRTL ? vwRect.width : vwRect.width - overscan;

    let deltaPx;
    if (leftInView < leftThreshold) deltaPx = leftThreshold - leftInView + 1;
    else if (rightInView > rightThreshold) deltaPx = rightThreshold - rightInView - 1;
    else return;

    const span = this.#inner.getBoundingClientRect().width - vwRect.width;
    if (span <= 0) return;
    // Map scroll progress <-> card translate progress (cards stay still during the start/end hold phases)
    const [startY, len] = this.#getScrollBounds(),
    hold = this.#hold,
    range = 1 - hold - this.#holdEnd,
    progress = this.#clamp((window.scrollY - startY) / len),
    tNow = this.#clamp((progress - hold) / range),
    tTarget = this.#clamp(tNow + (deltaPx * (isRTL ? 1 : -1)) / span),
    // At t = 0 / 1 any progress inside the hold phase is valid: only move if we're not already there
    pTarget = (tTarget === 0 && progress <= hold) || (tTarget === 1 && progress >= 1 - this.#holdEnd)
      ? progress
      : hold + tTarget * range,
    reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: Math.round(startY + pTarget * len), behavior: reduced ? 'auto' : 'smooth' });
    if (attempt < 2) {
      this.#focusTimer = setTimeout(() => this.#scrollCardIntoView(card, attempt + 1, token, needFocus), reduced ? 0 : 360);
    }
  }

  #bindFocus() {
    this.#cards.forEach(card => {
      const cardInner = $4('.hdt-collection-card-inner', card);
      if (cardInner && !cardInner.hasAttribute('tabindex') && cardInner.tagName !== 'A') {
        cardInner.setAttribute('tabindex', '0');
      }
    });
    const { signal } = this.#controller;
    this.#isPointerFocus = pointerFocusTracker(this.#scrollEl, signal);
    this.#scrollEl.addEventListener('focusin', (e) => {
      if (this.#isPointerFocus()) return;
      const card = e.target.closest('.hdt-cl-tilted-card');
      if (!card) return;
      this.#cancelFocusScroll();
      this.#scrollCardIntoView(card, 0, this.#focusToken);
    }, { capture: true, signal });
    document.addEventListener('focusin', (e) => {
      if (!this.contains(e.target)) this.#cancelFocusScroll();
    }, { capture: true, signal });
  }

  #initBackground(contentH) {
    const content = this.#content;
    this.style.setProperty('--content-height', `${contentH}px`);
    const inset = 'inset(0 0 0 0 round 0px)',
    inset2 = 'inset(20px round var(--rounded, 0px))',
    inset3 = 'inset(0px 20px round var(--rounded, 0px))',
    sequence = [
      [this.#bg, { clipPath: [inset, inset2, 'inset(calc(50vh - var(--content-height) * 0.5) 30px calc(50vh - var(--content-height) * 0.5) var(--clip-x, 3rem) round var(--rounded-lg, 0px))', inset2] }],
      [content, { clipPath: [inset, inset3, 'inset(0px 30px round var(--rounded-lg, 0px))', inset3] }, { at: '<' }]
    ];
    if (this.#headerWrap) sequence.push([this.#headerWrap, { x: ['0px', 'calc(var(--value-logical-flip) * 30px)'] }, { at: '<' }]);
    this.#cleanupFns.push(
      scroll(animate(sequence), { offset: ['0% end', '22% end', '78% end', '95% end'], target: this })
    );
  }

  #init() {
    const scrollW = this.#scrollEl.clientWidth,
    innerW = this.#inner.clientWidth;
    if (!this.#isDesktop || innerW <= scrollW) return;
    this.#controller = new AbortController();
    // Reduced motion: keep only the horizontal movement needed to reach every card (no tilt, zoom or background clip)
    const motionOK = matchMediaQuery('motion');

    // Read every size before the first style write (one layout pass instead of several)
    const vh = window.innerHeight,
    sectionH = this.offsetHeight,
    contentH = this.#content?.getBoundingClientRect().height || 0,
    headerWrapW = this.#headerWrap?.offsetWidth || 0,
    // Resolve to px once per init. Measure the scrollbar directly: --scrollbar-w may not be set yet on first init
    html = document.documentElement,
    scrollbarW = html.hasAttribute('scroll-lock')
      ? parseFloat(getComputedStyle(html).getPropertyValue('--scrollbar-w')) || 0
      : this.#clamp(window.innerWidth - html.clientWidth, 0, 20),
    probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;width:calc(30px + var(--padding-inline))';
    this.#scrollEl.appendChild(probe);
    const translateX = (scrollW - innerW - scrollbarW - probe.offsetWidth) * (isRTL ? -1 : 1);
    probe.remove();

    if (this.#bg && motionOK) this.#initBackground(contentH);

    const pinLen = Math.max(1, sectionH - vh);
    this.#hold = this.#clamp((vh * 0.2) / pinLen, 0, 0.25);
    this.#holdEnd = this.#clamp((vh * 0.25) / pinLen, 0, 0.25);
    const hold = this.#hold,
    range = 1 - hold - this.#holdEnd;

    // Header fades out while the first card travels across it
    const header = this.#header,
    cover = headerWrapW * 0.6,
    fadeEnd = this.#clamp(hold + range * (cover / Math.max(1, innerW - scrollW)), hold + 0.05, 0.6);

    // Each card straightens over half a slot of the scroll progress
    const cards = this.#cards,
    n = cards.length;

    if (n) this.#scrollEl.classList.add('hdt-has-cards');
    if (!motionOK) cards.forEach(card => card.style.setProperty('--angle-percent', 0));

    // Single scroll handler; only write styles when the value actually changes
    const last = { t: NaN, fade: NaN, edge: NaN, angles: [] };
    this.#cleanupFns.push(
      scroll((progress) => {
        const t = this.#clamp((progress - hold) / range);
        if (t !== last.t) {
          last.t = t;
          this.#inner.style.transform = `translateX(${t * translateX}px)`;
        }
        const edge = t >= 0.9 ? (t - 0.9) / 0.1 : 0;
        if (n && edge !== last.edge) {
          last.edge = edge;
          this.#scrollEl.style.setProperty('--opacity-progress', edge);
        }
        if (header) {
          const fade = this.#clamp((progress - hold) / (fadeEnd - hold));
          if (fade !== last.fade) {
            last.fade = fade;
            header.style.opacity = 1 - fade;
            if (motionOK) header.style.transform = `scale(${1 - 0.18 * fade})`;
          }
        }
        for (let i = 0; motionOK && i < n; i++) {
          const angle = 1 - this.#clamp((progress - i / n) / (0.5 / n));
          if (angle !== last.angles[i]) {
            last.angles[i] = angle;
            cards[i].style.setProperty('--angle-percent', angle);
          }
        }
      }, { offset: ['start start', 'end end'], target: this })
    );

    if (n) this.#bindFocus();
  }
}
registerEl('hdt-hero-collections', HeroCollections);

// XOAN =============================================

//Counter number
class Counter extends HTMLElement {
  #animation;
  #inView;
  constructor() {
    super();
  }
  connectedCallback() {
    if (!matchMediaQuery("motion")) {
      animate([
        [this.lastElementChild, { opacity: [1, 0] }, { duration: 0 }],
        [this.firstElementChild, { opacity: [0, 1] }, { duration: 0 }],
      ]);
      return;
    }
    const target = parseInt(this.dataset.counter) || 0;
    const duration = matchMediaQuery("motion") ? parseFloat(this.dataset.duration) || 5 : 0;
    this.#animation = animate(0, target, {
      duration,
      ease: "circOut",
      onUpdate: (latest) => {
        this.lastElementChild.textContent = Math.round(latest);
      },
    });
    this.#animation.pause();
    this.#inView = inView(this, () => {
      this.#animation.play()
      return () => this.#animation.complete()
    }, { amount: 0.5 });
  }
  disconnectedCallback() {
    if (!matchMediaQuery("motion")) return;
    this.#animation.cancel();
    this.#inView();
  }
}
registerEl("hdt-counter", Counter);
//Scroll section
class ShirkScroll extends HTMLElement {
    constructor() {
      super();
      this.mediaQueryList = window.matchMedia('(min-width: 768px)');
      this.offsetStart = 0;
      this.scrollInstances = []
      this.minScale = 0;
    }
    connectedCallback() {
        this.boundAnimateSection = this.animateSection.bind(this);
        this.mediaQueryList.addEventListener('change', this.boundAnimateSection);
        // Measure in the read phase and set up the animation after it, instead of forcing layout on connect
        frame.read(() => {
            if (this.isConnected && this.getBoundingClientRect().height < window.innerHeight) this.offsetStart = 0.3;
        });
        frame.update(() => {
            if (this.isConnected) this.boundAnimateSection();
        });
    }
    disconnectedCallback() {
        if (this.boundAnimateSection) this.mediaQueryList.removeEventListener('change', this.boundAnimateSection);
        this.resetTransforms();
    }
    animateSection(){
      // Crossing the breakpoint again: drop the previous animation and its scroll link before building a new one
      this.resetTransforms();
      const shouldAnimate = this.mediaQueryList.matches
      if(shouldAnimate == false){
          return
      }
      if ( this.hasAttribute('on-shirk-scroll') ) {
        const contentLeft = $$4('[transLeft]', this);
        const contentRight = $$4('[transRight]', this);
        const mediaShirk = $$4('.hdt-media-wrapper:not([unless]) > :is(img, svg), .hdt-media-wrapper:not([unless]) hdt-video > :is(img, video, iframe)', this);
        const scaleMax = parseFloat(getComputedStyle(this).getPropertyValue('--scale-max'));
        let sequence = [];
        sequence =[
          [ this, { clipPath: [`inset(0 0 0 0 round 0)`,`inset(0 var(--clip-path-off) 0 var(--clip-path-off) round var(--rounded-lg, 0))`] }, { ease: 'linear'} ]
        ]
        if(mediaShirk.length){
          sequence.push([
            mediaShirk,
            { transform: [`scale(${scaleMax})`, `scale(1)`] },
            { ease: "linear", at: "<" }
          ])
        }
        if(contentLeft.length){
          sequence.push([
            contentLeft,
            { transform: [`translateX(0px)`, `translateX(calc(1 * var(--value-logical-flip) * var(--clip-path-off)))`] },
            { ease: "linear", at: "<" }
          ]);
        }
        if(contentRight.length){
          sequence.push([
            contentRight,
            { transform: [`translateX(0px)`, `translateX(calc(-1 * var(--value-logical-flip) * var(--clip-path-off)))`] },
            { ease: "linear", at: "<" }
          ]);
        }
        this.currentAnimate = animate(sequence)
        this._cancelScroll = scroll(
          this.currentAnimate,
          {
            target: this,
            offset: ["start end", `end ${this.offsetStart}`]
          }
        )
      }
    }
    resetTransforms() {
      this._cancelScroll?.()
      this._cancelScroll = null
      this.currentAnimate?.cancel()
      this.currentAnimate?.stop()
      this.currentAnimate = null
    }
}
registerEl("hdt-shirk-scroll", ShirkScroll);

//Testimonial image
const dispScale = springValue(100);
svgEffect($id4('hdt-feDisplacementMap'), { attrScale: dispScale })
class TestimonialImage extends ShirkScroll{
    #imagesTes;
    #imageW;
    #zIndexMap;
    #controller;
    // Slide changes run one after another: overlapping reorders would stack the images in the wrong order
    #queue = Promise.resolve();
    #pending = 0;
    connectedCallback(){
        if (this.hasAttribute('on-shirk-scroll')) super.connectedCallback();
        this.#controller = new AbortController();
        this.#imagesTes = $$4('.hdt-testimonial-image', this);
        this.#zIndexMap = this.#imagesTes.map((img, i) => parseInt(img.style.zIndex || 0) || 0)

          $4('hdt-slider', this)?.addEventListener('select', (event) => {
            const {selectedScrollSnap, previousScrollSnap} = event.detail.sliderApi,
            targetSelect = selectedScrollSnap(),
            targetPrev = previousScrollSnap();
            this.#pending++;
            this.#queue = this.#queue
              // Several changes waiting (fast clicks): shorter animation to catch up
              .then(() => this.#animateAndReorder(targetSelect, targetPrev, this.#pending > 1 ? 0.1 : 0.25))
              .catch(() => {})
              .finally(() => this.#pending--);
          }, { signal: this.#controller.signal })
    }
    disconnectedCallback(){
        super.disconnectedCallback();
        this.#controller?.abort();
    }

    #animateAndReorder(targetIndex, otherIndex = null, duration = 0.25) {
        const maxI = this.#imagesTes.length - 1
        let direction = targetIndex < otherIndex ? 'left' : 'right';
        const wrapLeft  = targetIndex < otherIndex && targetIndex === 0 && otherIndex === maxI;
        const wrapRight = targetIndex > otherIndex && targetIndex === maxI && otherIndex === 0;
        if (wrapLeft)  direction = 'right';
        if (wrapRight) direction = 'left';
        // Measured on first slide change instead of on connect (avoids a forced layout on page load)
        this.#imageW ??= $4('.hdt-inner-list-images-testimonial .hdt-testimonial-image', this).offsetWidth + 40;
        const offset = direction === 'left' ? -this.#imageW : this.#imageW;
        let animateTarget;
        if (wrapLeft) {
          animateTarget = this.#imagesTes[otherIndex];
        } else if (wrapRight) {
            animateTarget = this.#imagesTes[targetIndex];
        } else {
            animateTarget = this.#imagesTes[targetIndex < otherIndex ? targetIndex : otherIndex];
        }
        return animate(
            animateTarget,
            { x: [0, offset] },
            { duration }
        ).then(() => {
          this.#imagesTes[targetIndex].style.zIndex = this.#imagesTes.length;
          this.#zIndexMap[targetIndex] = this.#imagesTes.length;

          let delta;
          if (wrapLeft) {
              delta = 1;
              this.#imagesTes[otherIndex].style.zIndex = 1;
              this.#zIndexMap[otherIndex] = 1;

          } else if (wrapRight) {
              delta = -1;
              this.#imagesTes[otherIndex].style.zIndex = maxI;
              this.#zIndexMap[otherIndex] = maxI;

          } else {
              const forward = targetIndex < otherIndex;
              delta = forward ? -1 : 1;

              const z = forward ? maxI : 1;
              this.#imagesTes[otherIndex].style.zIndex = z;
              this.#zIndexMap[otherIndex] = z;
          }
          // Update z-index for other image
          this.#imagesTes.forEach((img, idx) => {
            if (idx !== targetIndex && idx !== otherIndex) {
              // const delta = direction === 'left' ? -1 : 1;
              const newZ = (this.#zIndexMap[idx] || 0) + delta;
              img.style.zIndex = newZ;
              this.#zIndexMap[idx] = newZ;
            }
          })
          // Animate to return to the initial/original position
          return animate(
              animateTarget,
              { x: [offset, 0] },
              { duration }
          )
        })
    }
}
registerEl("hdt-testimonial-image", TestimonialImage);


class productHoverTitle extends HTMLElement {
  #controller;
  #titleItem;
  #imgWrapper;
  #springs;
  #cancelStyle;
  #hovering = false;
  #lastEvent = null;
  #queued = false;
  connectedCallback(){
    if (!matchMediaQuery("hover")) return;
    this.#titleItem = $4('.hdt-product-title', this);
    this.#imgWrapper = $4('.hdt-product-image-wrapper', this);
    if (!this.#titleItem || !this.#imgWrapper) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // Spring values / styleEffect are created on the first hover instead of on connect
    this.#titleItem.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch' || this.#hovering) return;
      this.#hovering = true;
      this.#init();
      this.#start();
    }, { signal });
    this.#titleItem.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch' || !this.#hovering) return;
      this.#hovering = false;
      this.#end();
    }, { signal });
    // Mouse move title: keep the latest event, measure once per frame
    this.#titleItem.addEventListener('mousemove', (e) => {
      if (!this.#hovering) return;
      this.#lastEvent = e;
      if (this.#queued) return;
      this.#queued = true;
      frame.read(this.#updatePosition);
    }, { signal });
  }
  disconnectedCallback(){
    this.#controller?.abort();
    this.#cancelStyle?.();
    if (this.#springs) Object.values(this.#springs).forEach((value) => value.destroy());
    this.#springs = this.#cancelStyle = this.#lastEvent = null;
    this.#hovering = this.#queued = false;
  }
  #init() {
    if (this.#springs) return;
    const left = springValue(0),
    top = springValue(this.#height());
    this.#springs = { left, top };
    this.#cancelStyle = styleEffect(this.#imgWrapper, { left, top });
  }
  #start() {
    dispScale.set(0);
    this.#springs.top.set(this.#height());
    //animate(dispScale, 0, { duration: 0.7, ease: [0.23, 1, 0.32, 1] });
    animate(this.#imgWrapper, { opacity: [0, 1] }, { duration: 0.15 });
  }
  #end() {
    this.#lastEvent = null;
    //animate(dispScale, 180, { duration: 0.4, ease: 'ease-in' });
    dispScale.set(100);
    animate(this.#imgWrapper, { opacity: [1, 0] }, { duration: 0.4, delay: 0.1 });
  }
  #updatePosition = () => {
    this.#queued = false;
    const e = this.#lastEvent;
    if (!this.#hovering || !e || !this.#springs) return;
    const titleItem = this.#titleItem,
    mouseX = e.clientX;

    const wrapRect = titleItem.getBoundingClientRect(),
    imgRect = this.#imgWrapper.getBoundingClientRect();

    // Original position: title is centered vertically in the image, image is centered horizontally.
    const baseX = (mouseX - wrapRect.left) - imgRect.width / 2;

    // Small offset by mouse
    const titleWidth = wrapRect.width,
    relX = ((mouseX - wrapRect.left) / titleWidth - 0.5) * 2;   // -1 to 1

    const offsetRange = 25, // px, adjust the rocking motion
    targetX = baseX + relX * offsetRange;
    this.#springs.left.set(targetX);
  }
  #height() {
    return -1 * ((this.#imgWrapper?.clientHeight || 0) + 15);
  }
}
registerEl("hdt-pr-hover", productHoverTitle);

// Scroll stack
class HeroScrollStack extends HTMLElement {
  #group;
  #isPointerFocus = () => false;
  #items = [];
  // Parallax images with their slice of the section scroll progress
  #parallax = [];
  #cancels = [];
  #animated = [];
  #controller;
  #observer;
  #editorTimer = null;

  connectedCallback() {
    this.#group = $4('.hdt-group-stack-scroll', this);
    if (!this.#group) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#init();

    this.#isPointerFocus = pointerFocusTracker(this, signal);
    this.addEventListener('focusin', this.#onFocusIn.bind(this), { signal });
    // Disable tab default
    this.addEventListener('keydown', (evt) => overrideFocus(this, evt), { signal });
    // Reduced motion toggled while the page is open: rebuild with or without decorative effects
    window.matchMedia('(prefers-reduced-motion: no-preference)').addEventListener('change', () => {
      this.#destroy();
      this.#init();
    }, { signal });

    if (window.Shopify?.designMode) {
      // Theme editor: bring the selected block into view
      this.addEventListener(admEvts.select, this.#onBlockSelect.bind(this), { signal });
      // Blocks re-rendered in place: rebuild with the new nodes
      this.#observer = new MutationObserver(debounce(() => {
        if (!this.isConnected) return;
        this.#destroy();
        this.#init();
      }, 100));
      this.#observer.observe(this.#group, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#editorTimer);
    this.#destroy();
  }

  #init() {
    const items = this.#items = $$4('.hdt-hero-media', this.#group);
    if (!items.length) return;
    const last = items.length - 1,
    // Reduced motion: keep only the horizontal movement needed to reach every item
    motionOK = matchMediaQuery('motion'),
    track = (el, keyframes, options) => {
      const animation = animate(el, keyframes);
      this.#animated.push({ el, animation });
      this.#cancels.push(scroll(animation, { target: this, ...options }));
    };

    if (motionOK) track(items[0], { transform: ['scale3d(0.5, 0.5, 1)', 'scale3d(1, 1, 1)'] }, { offset: ['start end', 'start 30vh'] });
    track(this.#group, {
      transform: ['none', `translateX(calc((-${last * 100}% - ${last} * var(--gap-hero)) * var(--value-logical-flip)))`]
    });

    if (!motionOK) return;

    // Precompute each image's progress range once instead of on every scroll frame
    // One item: keep the original static offset (no progress range)
    const step = last ? 1 / last : Infinity,
    dir = isRTL ? -1 : 1;
    this.#parallax = items.map((item, idx) => {
      const img = $4('.hdt-media-wrapper img', item);
      if (!img) return null;
      const start = idx === 0 ? 0 : step * (idx - 1),
      end = idx === 0 ? step : idx === last ? step * idx : step * (idx + 0.5);
      return { img, start, range: (end - start) || 1, base: (idx === 0 ? 10 : 30) * dir, value: null };
    }).filter(Boolean);

    if (this.#parallax.length) {
      this.#cancels.push(scroll((progress) => {
        for (const p of this.#parallax) {
          const local = Math.min(1, Math.max(0, (progress - p.start) / p.range)),
          value = -p.base + p.base * local;
          // Images outside their range stay clamped: skip the style write
          if (value === p.value) continue;
          p.value = value;
          p.img.style.transform = `translateX(${value}%)`;
        }
      }, { target: this, offset: ['start start', 'end end'] }));
    }
  }

  #destroy() {
    this.#cancels.forEach((fn) => fn?.());
    this.#cancels = [];
    // Clear values Motion leaves inline after cancel
    this.#animated.forEach(({ el, animation }) => {
      animation.cancel();
      el.style.transform = '';
    });
    this.#animated = [];
    this.#parallax.forEach((p) => { p.img.style.transform = ''; });
    this.#parallax = [];
  }

  // Page scroll position that shows item `index` (matches the translate progress)
  #scrollTopForIndex(index) {
    const rect = this.getBoundingClientRect(),
    start = window.scrollY + rect.top,
    end = start + rect.height - window.innerHeight,
    maxStep = Math.max(this.#items.length - 1, 1),
    top = end <= start ? start : start + (index / maxStep) * (end - start),
    maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    return Math.min(Math.max(0, top), Math.max(0, maxScroll));
  }

  #scrollToItem(item, instant) {
    const idx = this.#items.indexOf(item);
    if (idx < 0) return false;
    window.scrollTo({ top: this.#scrollTopForIndex(idx), behavior: instant ? 'auto' : 'smooth' });
    return true;
  }

  #onFocusIn(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const isButtonLike = target.tagName === 'BUTTON' || target.getAttribute('role') === 'button' || (target.tagName === 'A' && target.href);
    if (!isButtonLike || this.#isPointerFocus()) return;
    const item = target.closest('.hdt-hero-media'),
    reduced = !matchMediaQuery('motion');
    if (!item) return;

    requestAnimationFrame(() => this.#scrollToItem(item, reduced));
  }

  #onBlockSelect(e) {
    const item = e.target.closest('.hdt-hero-media');
    if (!item) return;
    const instant = e.detail?.load || !matchMediaQuery('motion');
    clearTimeout(this.#editorTimer);
    // Run after the editor's own scroll, then correct once if the page moved again
    requestAnimationFrame(() => {
      if (!this.#scrollToItem(item, instant)) return;
      this.#editorTimer = setTimeout(() => {
        const idx = this.#items.indexOf(item);
        if (idx > -1 && Math.abs(window.scrollY - this.#scrollTopForIndex(idx)) > 2) this.#scrollToItem(item, true);
      }, instant ? 50 : 500);
    });
  }
}

registerEl('hdt-hero-scrollstack', HeroScrollStack);

//Media with text scroll
class MediaTextScroll extends HTMLElement {
    #group;
    #items = [];
    #scrollCancel;
    #animation;
    #controller;
    #observer;
    #lastWidth = 0;
    #lastDistance = -1;
    #focusScrolling = false;
    #focusTimer = null;
    #editorTimer = null;
    #isPointerFocus = () => false;

    connectedCallback() {
      this.#group = $4('.hdt-ground-media-text', this);
      if (!this.#group) return;
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.#items = $$4('.hdt-item-media-w-text', this.#group);
      // Measure in the read phase instead of forcing layout on connect
      frame.read(() => {
        if (this.isConnected) this.#initScroll(true);
      });

      // Only width changes the layout: skip height-only resizes (mobile URL bar)
      window.addEventListener('resize', debounce(() => {
        if (window.innerWidth !== this.#lastWidth) this.#initScroll();
      }, 300), { signal });

      // Bound once: handlers check whether the scroll animation is active
      this.#isPointerFocus = pointerFocusTracker(this, signal);
      this.addEventListener('focusin', this.#onFocusIn.bind(this), { signal });
      this.addEventListener('keydown', (evt) => { if (this.#isActive) overrideFocus(this, evt); }, { signal });

      if (window.Shopify?.designMode) {
        // Theme editor: bring the selected block into view on desktop (mobile is handled by hdt-slider)
        this.addEventListener(admEvts.select, this.#onBlockSelect.bind(this), { signal });
        // Blocks re-rendered in place: refresh items and measurements
        this.#observer = new MutationObserver(debounce(() => {
          if (!this.isConnected) return;
          this.#items = $$4('.hdt-item-media-w-text', this.#group);
          this.#initScroll(true);
        }, 100));
        this.#observer.observe(this.#group, { childList: true });
      }
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      clearTimeout(this.#focusTimer);
      clearTimeout(this.#editorTimer);
      this.#cancel();
    }

    get #isActive() {
      return !!this.#animation;
    }

    #initScroll(force = false) {
      if (this.#focusScrolling && !force) return;
      this.#lastWidth = window.innerWidth;

      if (!matchMediaQuery('(min-width: 768px)') || !this.#items.length) {
        this.classList.remove('hdt-no-scroll');
        this.#cancel();
        return;
      }

      // Measure in the scroll layout
      this.classList.remove('hdt-no-scroll');
      const containerW = this.#group.offsetWidth,
      gap = parseFloat(getComputedStyle(this.#group).columnGap) || 0,
      totalW = this.#items[0].offsetWidth * this.#items.length + (this.#items.length - 1) * gap,
      distance = Math.round(totalW - containerW);

      if (distance <= 0) {
        this.classList.add('hdt-no-scroll');
        this.#cancel();
        return;
      }

      // Same distance: keep the running animation (scroll() tracks height changes itself)
      if (!force && this.#isActive && distance === this.#lastDistance) return;

      this.#cancel();
      this.#lastDistance = distance;
      this.#animation = animate(this.#group, {
        transform: ['translateX(0px)', `translateX(calc(${distance}px * -1 * var(--value-logical-flip)))`],
      });
      this.#scrollCancel = scroll(this.#animation, { target: this });
    }

    // Page scroll position that shows item `index`
    #scrollTopForIndex(index) {
      const rect = this.getBoundingClientRect(),
      start = window.scrollY + rect.top,
      end = start + rect.height - window.innerHeight,
      maxStep = Math.max(this.#items.length - 1, 1),
      top = end <= start ? start : start + (index / maxStep) * (end - start),
      maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      return Math.min(Math.max(0, top), Math.max(0, maxScroll));
    }

    #scrollToItem(item, instant) {
      const idx = this.#items.indexOf(item);
      if (idx < 0) return false;
      window.scrollTo({ top: this.#scrollTopForIndex(idx), behavior: instant ? 'auto' : 'smooth' });
      return true;
    }

    #onFocusIn(e) {
      if (!this.#isActive) return;
      const target = e.target;
      if (!(target instanceof HTMLAnchorElement || target instanceof HTMLButtonElement)) return;
      if (this.#isPointerFocus()) return;
      const item = target.closest('.hdt-item-media-w-text'),
      reduced = !matchMediaQuery('motion');
      if (!item) return;

      this.#focusScrolling = true;
      clearTimeout(this.#focusTimer);
      requestAnimationFrame(() => this.#scrollToItem(item, reduced));
      this.#focusTimer = setTimeout(() => { this.#focusScrolling = false; }, reduced ? 0 : 600);
    }

    #onBlockSelect(e) {
      if (!this.#isActive) return;
      const item = e.target.closest('.hdt-item-media-w-text');
      if (!item) return;
      const instant = e.detail?.load || !matchMediaQuery('motion');
      clearTimeout(this.#editorTimer);
      // Run after the editor's own scroll, then correct once if the page moved again
      requestAnimationFrame(() => {
        if (!this.#scrollToItem(item, instant)) return;
        this.#editorTimer = setTimeout(() => {
          const idx = this.#items.indexOf(item);
          if (idx > -1 && Math.abs(window.scrollY - this.#scrollTopForIndex(idx)) > 2) this.#scrollToItem(item, true);
        }, instant ? 50 : 500);
      });
    }

    #cancel() {
      this.#scrollCancel?.();
      this.#animation?.cancel();
      this.#scrollCancel = this.#animation = null;
      this.#lastDistance = -1;
      // Clear any value Motion leaves inline after cancel
      if (this.#group) this.#group.style.transform = '';
    }
}
registerEl("hdt-mediatext-scroll", MediaTextScroll);

//Media lookbook
class MediaLookbook extends HTMLElement{
    constructor() {
        super();
        this.initialized = false;
        this.tabPlayer = $4("hdt-tab-player-list", this);
        this.buttons = $$4('button', this.tabPlayer)
        // this.prevIndex = this.tabPlayer.currIndex;
        this.prevIndex = this.getActiveIndex();

    }
    connectedCallback(){
      this.pauseOnhover();
      if (this.hasAttribute("reveal-on-scroll")) {
        this.animateOnScrollOnce();
      }
      this.observeTabChange();
      if (window.Shopify && Shopify.designMode) {
        this._controller = new AbortController();
        this._setupShopifyBlockSelection(this._controller.signal);
      }
    }
    disconnectedCallback(){
      this.mo?.disconnect();
      this._controller?.abort();
    }
    getActiveIndex() {
      return [...this.buttons].findIndex(
        btn => btn.getAttribute('aria-current') === 'true'
      );
    }
    observeTabChange() {
      this.mo = new MutationObserver(() => {
        const currIndex = this.getActiveIndex();
        if (currIndex !== -1 && currIndex !== this.prevIndex) {
          this.onTabChange(currIndex, this.prevIndex);
          this.prevIndex = currIndex;
        }
      });

      this.buttons.forEach(btn => {
        this.mo.observe(btn, { attributes: true, attributeFilter: ['aria-current'] });
      });

    }
    onTabChange(currIndex, prevIndex = null) {
      const tabContents = $$4(".hdt-tab-content-wrapper > .hdt-tab-content", this);
      const tabContentShow = tabContents[currIndex];
      const tabMedias = $$4(".hdt-tab-image-holder .hdt-lookbook-media", this);
      const tabMediaShow = $4('.hdt-lb-image', tabMedias[currIndex]);
      const pinItems = $$4(".hdt-pin-item", tabMedias[currIndex]);
      let sequence
      if(prevIndex == null){
        sequence = [
          [ tabContentShow, { opacity: [0,1], y: [30, 0] }, { duration: 0.4, at:"-0.2" } ],
          [ tabMediaShow, { '--inset-right': ['100%', '0%'] }, { duration: 0.4, at: "<" }],
          //[ tabMediaShow, { clipPath: [ "inset(0 100% 0 0 round var(--rounded-lg))", "inset(0 0 0 0 round var(--rounded-lg))"] }, { duration: 0.4, at:"<" } ],
          [ pinItems,   { opacity: [0,1], scale: [0.8,1] }, { duration: 0.5, delay: stagger(0.3) } ]
        ];
      }else{
        const tabContentHide = tabContents[prevIndex];
        sequence = [
          [ tabContentHide, { opacity: [1,0], y: [0, 30] }, { duration: 0.4 } ],
          [ tabContentShow, { opacity: [0,1], y: [30, 0] }, { duration: 0.4, at:"-0.2" } ],
          [ tabMediaShow, { '--inset-right': ['100%', '0%'] }, { duration: 0.4, at: "<" }],
          //[ tabMediaShow, { clipPath: [ "inset(0 100% 0 0 round var(--rounded-lg))", "inset(0 0 0 0 round var(--rounded-lg))"] }, { duration: 0.4, at:"<" } ],
          [ pinItems,   { opacity: [0,1], scale: [0.8,1] }, { duration: 0.5, delay: stagger(0.3) } ]
        ];
      }
      return animate(sequence);
    }

    _setupShopifyBlockSelection(signal) {
      document.addEventListener(admEvts.select, event => {
        const block = event.target;
        if (block.classList.contains('hdt-tab-title')) {
          setTimeout(() => {
            block.click();
          }, 20);
        }
      }, { signal });
    }

    animateOnScrollOnce(){
        inView(this, () => {
            animate(
                this.buttons,
                { opacity: [0, 1], x: [-30, 0] },
                { duration: 0.3, delay: stagger(0.2) }
            );
            this.onTabChange(this.prevIndex);
        }, { amount: 0.3 })
    }

    pauseOnhover(){
        const tabContent = $4('.hdt-tab-image-holder', this)
        const tabPrContent = $4('.hdt-tab-content-wrapper', this)
        const listTab = $4('hdt-tab-player-list', this)
        if(listTab.hasAttribute("autoplay")){
            const stopOnHover = (el) => {
                el?.addEventListener('mouseenter', () => {
                    listTab.pause();
                });
                el?.addEventListener('mouseleave', () => {
                    listTab.play();
                });
            };
            stopOnHover(tabContent);
            stopOnHover(tabPrContent);
        }
    }
}
registerEl("hdt-media-lookbook", MediaLookbook);

// Sidebar fillter
// Theme editor: keep the sidebar state in memory only, reset whenever a section reloads after a setting change
// (on unload: the new section reads the state as soon as it is added, before shopify:section:load fires)
const editorSidebarStates = new Map();
document.addEventListener(admEvts.seUnload, () => editorSidebarStates.clear());
class MainCollection extends HTMLElement {
    #controller;
    connectedCallback() {
      // One delegated click listener instead of a MutationObserver on the whole product grid:
      // the filter button is replaced when filters re-render, the section then fires products:update
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.addEventListener('click', this.#onClick, { signal });
      this.closest('.shopify-section')?.addEventListener('products:update', () => this.prepareFilterButton(), { signal });
      this.prepareFilterButton();
    }
    disconnectedCallback() {
      this.#controller?.abort();
    }
    // The first filter button of the section (same one as before)
    get filterButton() {
      return $4('.hdt-facets__open', this);
    }
    prepareFilterButton() {
      const btn = this.filterButton;
      if (!btn || btn.dataset.bound === 'true') return;
      btn.dataset.bound = 'true';
      if (btn.getAttribute('aria-controls') === 'hdt-sidebar-filters') this.restoreSidebarState(btn);
    }
    #onClick = (e) => {
      const btn = e.target.closest?.('.hdt-facets__open');
      if (!btn || btn !== this.filterButton) return;
      this.prepareFilterButton();
      const id = btn.getAttribute('aria-controls');
      const isSidebar = id === 'hdt-sidebar-filters';
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', !isExpanded);
      const target = $id4(id);
      if (target) {
        target.classList.toggle('hdt-stage-open');
      }
      if (isSidebar) this.saveSidebarState(btn, !isExpanded);
    }
    // Keep the sidebar open/closed as the customer left it across filter changes and page loads.
    // Stored as "<default>:<state>" so a new default from the theme editor resets it.
    // Not cached in the theme editor (sessionStorage), so merchants always see their settings.
    get sidebarStorageKey() {
      return `hdt-sidebar-filters:${this.getAttribute('sidebar-key') || 'collection'}`;
    }
    restoreSidebarState(btn) {
      const defaultState = btn.getAttribute('aria-expanded') === 'true' ? 'open' : 'closed';
      btn.dataset.default = defaultState;
      let stored = null;
      if (window.Shopify?.designMode) {
        stored = editorSidebarStates.get(this.sidebarStorageKey);
      } else {
        try { stored = sessionStorage.getItem(this.sidebarStorageKey); } catch (e) {}
      }
      if (!stored) return;
      const [storedDefault, state] = stored.split(':');
      if (storedDefault !== defaultState || state === defaultState) return;
      const isOpen = state === 'open';
      btn.setAttribute('aria-expanded', isOpen);
      $id4('hdt-sidebar-filters')?.classList.toggle('hdt-stage-open', isOpen);
    }
    saveSidebarState(btn, isOpen) {
      const value = `${btn.dataset.default}:${isOpen ? 'open' : 'closed'}`;
      if (window.Shopify?.designMode) return editorSidebarStates.set(this.sidebarStorageKey, value);
      try {
        sessionStorage.setItem(this.sidebarStorageKey, value);
      } catch (e) {}
    }
}
registerEl('hdt-main-collection', MainCollection);

//Footer Clip-path
class MorphFooter extends HTMLElement {
  #scroll;
  connectedCallback() {
    const logoFooter = $4('.hdt-footer__logo', this);
    if (!this.hasAttribute('reveal-on-scroll') || !matchMediaQuery("motion") || matchMediaQuery("mobile") || !logoFooter) return;
    const opacity   = motionValue(0),
          x         = motionValue('-100%'),
          logoStart = 0.1;
    styleEffect(logoFooter, { opacity, x });

    this.#scroll = scroll((progress) => {
      const logoProgress = Math.max(0, (progress - logoStart) / (1 - logoStart));
      opacity.set(logoProgress);
      x.set(`${(1 - logoProgress) * -100}%`);
    },
    {
      target: this,
      offset: ["start end", "end end"]
    });
  }
  disconnectedCallback() {
    if (this.#scroll) this.#scroll();
  }
}
registerEl("hdt-morph-footer", MorphFooter);

//Collection list dynamic
class CollectionDynamic extends HTMLElement {
  #controller;
  connectedCallback() {
    this.#controller = new AbortController();
    const slider = $4('hdt-slider', this),
    { signal } = this.#controller;
    if (slider?.apiS) this.#fn(slider.apiS);
    slider?.addEventListener('reInit', (event) => this.#fn(event.detail.sliderApi), { signal });
    slider?.addEventListener('select', (event) => this.#fn(event.detail.sliderApi), { signal });
  }
  disconnectedCallback() {
    this.#controller?.abort();
  }
  #fn(sliderApi) {
    this.classList.toggle('hdt-can-slide-prev', sliderApi.canScrollPrev());
    this.classList.toggle('hdt-can-slide-next', sliderApi.canScrollNext());
  }
}
registerEl("hdt-collection-dynamic", CollectionDynamic);

const inset = 'inset(0 round 0px)';
class scrollMediaText extends HTMLElement {
    #scroll;
    connectedCallback(){
        const media = $4('.hdt-scroll-media-with-text__media .hdt-media-wrapper', this),
        sequence = [
          //[$4('.hdt-scroll-media-with-text__media .hdt-media-wrapper', this), { width: [fixedStart, width, width, width], height: [fixedStart, height, height, height], borderRadius: [120, 0, 0, 0] }]
          [media, { clipPath: ["inset(calc(50% - var(--media-radius)) calc(50% - var(--media-radius)) round var(--media-radius))", inset, inset, inset]}],
          [$4(':where(hdt-video, svg, img)', media), { scale: [0.5, 1, 1, 1]}, { at: "<" }]
        ];
        const content = $4('.hdt-scroll-media-with-text__content', this);
        if (content) {
          sequence.push(
            [content, { opacity: [0, 0, 1, 1], y: [255, 255, 0, 0] }, { at: "<" }]
          )
        }
        this.#scroll = scroll(
          animate(sequence),
          {
              target: this.parentElement,
              offset: ["start start", "0.4 start", "0.5 start", "end start"],
          }
        );
    }
    disconnectedCallback() {
      this.#scroll();
    }
}
registerEl("hdt-scroll-media-text", scrollMediaText);

const inset2 = "inset(100% round 0px)";
class scrollMediaHero extends HTMLElement {
  connectedCallback(){
    const sequence = [],
    is3 = "inset(50% round 0px)",
    is4 = "inset(calc(50% - 100px) calc(50% - 100px) round 100px",
    content = $4('.hdt-scroll-hero-media__first .hdt-scroll-hero-media__content', this);
    if (content) {
      sequence.push( [content, { opacity: [0, 1, 1, 1, 0, 0, 0], y: [200, 0, 0, 0, 0, 0, 0], scale: [0.6, 1, 1.05] }] )
    }
    const items = $$4('.hdt-scroll-hero-media__item', this);
    if (items.length) {
      let translate = "translate(calc(-1 * var(--left-start)), calc(-1 * var(--top-start))) scale(0.5)",
      translate2 = "translate( calc(var(--tx-end1, 0) - var(--tx-start1) - (1% * var(--tx-end))), calc(var(--ty-end1, 0) - var(--ty-start1) - (1% * var(--ty-end))) )";
      sequence.push(
        [items, {
          opacity: [0, 1, 0, 0, 0, 0, 0, 0],
          //transform: [translate, `${translate2} scale(2)`, `${translate2} scale(2)`, `${translate2} scale(2)`, `${translate2} scale(2)`, `${translate2} scale(2.5)`, `${translate2} scale(3)`]
          transform: [translate, `${translate2} scale(1)`, `${translate2} scale(0.8)`, `${translate2} scale(0.5)`, `${translate2} scale(0.5)`, `${translate2} scale(0.5)`, `${translate2} scale(0.5)`]
        }, { at: "<", delay: stagger(0.015) }, { offset: ["start start", "0.15 start", "0.2 start", "0.4 start"] }]
      );
    }
    const second = $4('.hdt-scroll-hero-media__second', this);
    if (second) {
      sequence.push( [second, { clipPath: [inset2, is3, is4, inset, inset, inset] }, { at: "<", delay: 0.08 }] )
    }
    const content2 = second && $4('.hdt-scroll-hero-media__content', second);
    if (content2) {
      sequence.push( [content2, { opacity: [0, 0, 0, 0, 1, 1, 1], y: [200, 200, 200, 0, 0, 0], scale: [0.4, 1, 1] }, { at: "<" }] )
    }
    this._cancels = [];
    if (sequence.length) this._cancels.push(
      scroll( animate(sequence), { target: this.parentElement, offset: ["start start", "0.12 start", "0.33 start", "0.5 start", "0.8 start", "0.88 start", "end start"], } )
    );
    const first = $4('.hdt-scroll-hero-media__media', this);
    if (first) this._cancels.push(scroll( animate(first, { transform: [`scale(1)`, `scale(1.15)`] }), { target: this.parentElement,  offset: ["start end", "0.5 start"], } ));
    if (window.Shopify && Shopify.designMode) {
      this._controller = new AbortController();
      this._setupShopifyBlockSelection(this._controller.signal);
    }
  }
  disconnectedCallback() {
    this._cancels?.forEach((cancel) => cancel?.());
    this._cancels = null;
    this._controller?.abort();
  }
  _setupShopifyBlockSelection(signal) {
    document.addEventListener(admEvts.select, event => {
      event.target.classList.add('hdt-current-block');
    }, { signal });
    document.addEventListener(admEvts.deselect, event => {
      event.target.classList.remove('hdt-current-block');
    }, { signal });
  }
}
registerEl("hdt-scroll-hero-media", scrollMediaHero);

class sliderPlay extends HTMLElement {
  #id;
  #controller;
  constructor(){
    super();
    this.#id = $4('button', this).getAttribute("aria-controls");
  }
  connectedCallback() {
    this.#controller = new AbortController();
    if (!this.slider || !this.slider.interval) return;
    const { signal } = this.#controller;

    this.slider.addEventListener('reInit', this.#resetProgress.bind(this), { signal });
    this.slider.addEventListener('select', this.#resetProgress.bind(this), { signal });
    this.slider.addEventListener('play:state', () => this.toggleAttribute('playing', this.slider.isPlaying), { signal });
    this.addEventListener('click', this.#tooglePlay.bind(this), { signal });
    this.slider.addEventListener('mousedown', () => this.slider.isMousedown = true, { signal });
    this.slider.addEventListener('mouseup', () => this.slider.isMousedown = false, { signal });

  }
  disconnectedCallback() {
    this.#controller.abort();
  }
  #resetProgress(event) {
    const { slideNodes, selectedScrollSnap } = event.detail.sliderApi;
    this.parentElement.setAttribute('color-scheme', slideNodes()[selectedScrollSnap()].getAttribute('color-scheme'));

    this.removeAttribute('running');
    this.toggleAttribute('playing', this.slider.isPlaying);
    frame.render(() => {
      this.setAttribute('running', '');
    })
  }
  #tooglePlay() {
    // Visitor choice: a pause stays until they press play again (no resume on scroll, hover or focus)
    if (this.slider.isPlaying) {
      this.slider.pauseByUser();
    } else {
      this.slider.playByUser();
    }
  }
  get slider() {
    return $id4(this.#id);
  }
}
registerEl("hdt-slider-play", sliderPlay);

class DrawerHotspots extends HTMLElement {
  #controller;
  #needUpdate = false;
  #index = -1;
  #preBtn;
  #nextBtn;
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const tooltip = $4('.hdt-tooltip-des', this);
    this.addEventListener(dialogAdded, ()=> {
      const drawer = $4('hdt-drawer', this),
      slider = $4('hdt-slider', this),
      { dialog } = drawer;
      this.#preBtn = $4('button[name="previous"]', drawer);
      this.#nextBtn = $4('button[name="next"]', drawer);
      if (!this.#preBtn) return;
      dialog?.addEventListener(dialogOpening, ()=> {
        this.#needUpdate = true;
        this.setAttribute('open', '');
      }, { signal });
      dialog?.addEventListener(dialogClose, ()=> {
        this.#needUpdate = false;
        this.removeAttribute('open');
      }, { signal });

      slider?.addEventListener('reInit', (event) => {
        if (!this.#needUpdate) return;
        slider.goToIndex(parseInt(dialog.btnOpening.dataset.index) || 0, true);
        this.#updateNavHeading(event);
      }, { signal });
      slider?.addEventListener('select', this.#updateNavHeading, { signal });
    }, { once: true, signal });
    if (!Shopify.designMode) return;
      const m_x     = motionValue(0),
            m_y     = motionValue(0),
            img     = $4('.hdt-hotspots-with-drawer__hotspos-wrap', this),
            left    = transformValue(() => `${m_x.get()}px`),
            top     = transformValue(() => `${m_y.get()}px`);
      styleEffect(tooltip, { left, top });
      img.addEventListener("mousemove", (e) => {
        const rect = img.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        tooltip.style.opacity = this.hasAttribute('open') ? "0" : "1";
        const percentX = ((x / rect.width) * 100).toFixed(0);
        const percentY = ((y / rect.height) * 100).toFixed(0);
        tooltip.textContent = `X: ${percentX}% Y: ${percentY}%`;
        m_x.set(e.clientX);
        m_y.set(e.clientY);
      }, { signal });
      const cancelHover = hover(img, () => {
        tooltip.style.display = "block";
        tooltip.style.opacity = "1";
        return () => {
          tooltip.style.opacity = "0";
          tooltip.style.display = "none";
        }
      });
      signal.addEventListener('abort', () => cancelHover(), { once: true });
  }
  disconnectedCallback() {
    this.#controller.abort();
  }
  #updateNavHeading = (event) => {
    if (!this.#needUpdate && event.detail?._getSliderApi) return;
    const { sliderApi } = event.detail,
    index = sliderApi.selectedScrollSnap();

    if (this.#index == index) return;
    const slideNodes = sliderApi.slideNodes(),
    indexPrev = wrap(0, slideNodes.length, index - 1),
    indexNext = wrap(0, slideNodes.length, index + 1);
    // update prev heading
    this.#preBtn.nextElementSibling.textContent = $4('.hdt-hs-drawer-heading', slideNodes[indexPrev])?.textContent || '';
    // update next heading
    this.#nextBtn.nextElementSibling.textContent = $4('.hdt-hs-drawer-heading', slideNodes[indexNext])?.textContent || '';
    // cache current index
    this.#index = index;
  }
}

registerEl("hdt-hotspots-with-drawer", DrawerHotspots);
class Floating extends HTMLElement {
  #controller;
  #hovering = false;
  constructor() {
    super();
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller,
    reference        = this.previousElementSibling;

    // Position on hover instead of on connect: the tooltip only shows on hover (no layout reads on page load)
    const cancelHover = hover(reference, () => {
      this.#hovering = true;
      this.#updatePosition(reference).then(() => {
        if (this.#hovering) this.setAttribute('open', '');
      });
      return () => {
        this.#hovering = false;
        this.removeAttribute('open');
      };
    })
    signal.addEventListener('abort', () => cancelHover(), { once: true });
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#hovering = false;
  }
  #updatePosition(reference) {
    return computePosition(reference, this, {
      placement: this.#placement,
      middleware: [
        flip(),
        //offset(20),
        offset({
          mainAxis: 12,
          crossAxis: 1
        }),
        shift({padding: 1}),
        this.#arrowEl && arrow({ element: this.#arrowEl })
      ]
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.style, {
        top: `${y}px`,
        left: `${x}px`,
      });
      if (middlewareData.arrow) {
        this.setAttribute('placement-current', placement);
        // Accessing the data
        const {x: arrowX, y: arrowY} = middlewareData.arrow;
        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[placement.split('-')[0]];
        Object.assign(this.#arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      }
    });
  }
  get #arrowEl() {
    return this._arrowEl ??= $4('.hdt-floating__arrow', this);
  }
  get #placement() {
    return this.getAttribute("placement") || 'top';
  }
}
registerEl("hdt-floating", Floating);

// Volume pricing
class VolumePricing extends HTMLElement{
  #abortController;
  connectedCallback(){
    this.#abortController = new AbortController();
    $4('[ref="volumePricingToggle"]', this)?.addEventListener('click', () => this.classList.toggle('hdt-volume-pricing--expanded'), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
}
registerEl("hdt-volume-pricing", VolumePricing);

//Per price
class PricePerItemComponent extends HTMLElement {
  #priceBreaks = [];
  #quantityInput = null;
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#parsePriceBreaks();
    this.#findQuantityInput();
    const { signal } = this.#abortController;
    this.#quantityInput?.addEventListener('input', () => this.#updatePriceDisplay(), { signal });
    this.#quantityInput?.addEventListener('change', () => this.#updatePriceDisplay(), { signal });
    //this.#updatePriceDisplay();
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #parsePriceBreaks() {
    // const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
    // const { variantPrice, priceBreaks } = this.dataset;
    const { priceBreaks } = this.dataset;
    
    // Start with base price tier
    // if (variantPrice) {
    //   this.#priceBreaks.push({ quantity: minQuantity, price: variantPrice });
    // }

    if (priceBreaks) {
      try {
        const breaks = JSON.parse(priceBreaks);
        for (const { quantity, price } of breaks) {
          if (quantity && price) {
            this.#priceBreaks.push({ quantity: parseInt(quantity), price });
          }
        }
      } catch (error) {
        console.warn('[price-per-item] Failed to parse data-price-breaks: ', error);
      }
    }
    
    // Sort by quantity descending for efficient lookup
    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  /** * Find input[name="quantity"] in <product-form> */
  #findQuantityInput() {
    const form = this.closest('product-form');
    if (!form) return;
    this.#quantityInput = $4('input[name="quantity"]', form);

    if (!this.#quantityInput) {
      console.warn('[price-per-item] Could not find input[name="quantity"] inside <product-form>.');
    }
  }

  /** * Gets the total quantity (cart + current input value)  */
  #getCurrentQuantity() {
    if (!this.#quantityInput) return 1;

    const cartQuantity = parseInt(this.#quantityInput.dataset.cartQuantity || '0', 10) || 0;
    const inputQuantity = parseInt(this.#quantityInput.value, 10) || 1;

    return cartQuantity + inputQuantity;
  }

  // Price breaks are sorted descending, find first tier that quantity qualifies for
  #updatePriceDisplay() {
    if (!this.firstElementChild || this.#priceBreaks.length === 0 || !this.#quantityInput.checkValidity()) return;
    const quantity = this.#getCurrentQuantity();
    //console.log(quantity)
    const matchedBreak = this.#priceBreaks.find((priceBreak) => quantity >= priceBreak.quantity) ?? this.#priceBreaks[this.#priceBreaks.length - 1];
    this.firstElementChild.textContent = matchedBreak.price;
  }
}
registerEl('price-per-item', PricePerItemComponent);

class ProductBundle extends HTMLElement {
  #min;
  #max;
 #add;
  #addBundles;
  #tmp;
  #items;
  #bar;
  #formData = { items: [], sections: [] };
  #total = 0;
  #totalEls;
  #countAdded = 0;
  #submitting = false;
  #abortController;
  #error;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#min = parseInt(this.getAttribute('min'));
    this.#max = parseInt(this.getAttribute('max'));
    this.#add = $4('[ref="addBundleToCart"]', this);
    this.#addBundles = $$4('[ref="addBundle"]', this);
    this.#tmp = $4('[ref="tmp"]', this);
    this.#items = $4('[ref="items"]', this);
    this.#bar = $4('[ref="bar"]', this);
    this.#totalEls = $$4('[ref="bundleTotal"]', this);
    this.#error = $4('[ref="addBundleError"]', this);

    let isShow = false;
    $4('[ref="expandButton"]', this).addEventListener( 'click', () => {
      isShow = !isShow;
      this.toggleAttribute('expanded', isShow);
    }, { signal });

    // add/remove item bundle
    this.addEventListener( 'click', (e) => {
      const btn = e.target,
      ref = btn.getAttribute('ref');
      let updateTotal = false
      if (ref == 'addBundle') {
        this.#addBundle(btn);
        updateTotal = true;
      } else if (ref == 'removeBundle') {
        this.#removeBundle(btn);
        updateTotal = true;
      }
      if (updateTotal) {
        this.#totalEls.forEach( (el) => el.innerHTML = currency.formatMoney(this.#total) )
      }
    }, { signal });

    this.#add.addEventListener('click', (e) => {
      // aria-disabled doesn't block clicks: ignore new clicks while the bundle is being added (no double add)
      if (this.#submitting) return;
      this.#submitting = true;
      this.#formData.sections = this.#cart?.sectionsToRender?.map(section => section.id) ?? ['cart-json'];
      this.#add.setAttribute("aria-disabled", true);
      this.#add.setAttribute("aria-busy", true);
      document.dispatchEvent(new CustomEvent(loadingStart));
      let loading = true;
      const release = () => {
        this.#submitting = false;
        this.#add.removeAttribute("aria-disabled");
        this.#add.removeAttribute("aria-busy");
        if (!loading) return;
        loading = false;
        document.dispatchEvent(new CustomEvent(loadingEnd));
      };
      fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.#formData)
      })
      .then(response => {
        release();
        return response.json();
      }).then(data => {
        if (data.status) {
          //throw new Error(`error: ${data}`);
          if (data.description) {
            this.#error.lastElementChild.textContent = data.description;
            this.#error.hidden = false;
          }
          this.#removeAllBundle();
          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart reload event
          // because our back-end still adds the max allowed amount to the cart.
          document.dispatchEvent(new CustomEvent(cartReload, {
            bubbles: true
          }));
          return;
        } else {
          this.#removeAllBundle();
        }
        const cartData = {
          ...data,
          ...JSON.parse(data?.sections?.['cart-json']?.split('[split_94]')?.[1] || '{}')
        };
        this.dispatchEvent(new CustomEvent(cartUpdate, {
          bubbles: true,
          detail: {
            source: 'product-bundle',
            actionAfterATC: themeHDN.settings.actionAfterATC,
            cartData
          }
        }));
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      // Request failed (network, invalid JSON): release the button and the loading bar anyway
      .finally(release);
    }, { signal });

    this.addEventListener("richlist:select", (e) => {
      const btn = e.target;
      if (!btn.hasAttribute('data-price')) return;

      // 1. Update JSON
      const product = btn.closest('.hdt-card-product'),
      scriptEl = $4('[ref="json"]', product),
      newData = JSON.parse(JSON.stringify(btn.dataset));
      newData.id = btn.value;
      newData.options = btn.textContent;
      this.#updateJson(scriptEl, newData);
      
      // 2. Update DOM
        // 2.1. Img
        if (newData.src) {
          const mediaMain = $4('.hdt-card-product__media--main', product),
          newImg = this.#createImg(renderSrc(newData.src, newData.width), mediaMain);
          mediaMain.replaceWith(newImg);
        }

        // 2.2. Price
        const priceEl = $4('[ref="price"]', product),
        comparePriceEl = $4('[ref="compare-price"]', product),
        { price, compareprice } = newData;
        if (priceEl) {
          priceEl.innerHTML = currency.formatMoney(price);
          comparePriceEl.parentElement.hidden = compareprice <= price;
          if (compareprice > price) comparePriceEl.innerHTML = currency.formatMoney(compareprice);
        }

    }, { signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  #addBundle(btn) {
    this.#error.hidden = true;
    ++this.#countAdded;
    if (this.#countAdded >= this.#min) this.#add.removeAttribute('disabled');
    if (this.#countAdded <= this.#min) {
      this.#slot(this.#min - this.#countAdded + 1).hidden = true;
      if (this.#bar) this.#bar.value = this.#countAdded;
    }
    if (btn.hasAttribute('add-once')) {
      btn.setAttribute('disabled', '');
      btn.setAttribute('added', '')
    }
    if (this.#countAdded >= this.#max) this.#addBundles.forEach((btn) => btn.setAttribute('disabled', ''));
    const dataItem = JSON.parse(btn.previousElementSibling.textContent.trim()),
    newItem = this.#createBundleItem(dataItem, btn.dataset.prId);
    this.#addItem(this.#formData.items, { 'id': dataItem.id, 'quantity': dataItem.min });
    this.#total += dataItem.price * dataItem.min;
    this.#items.prepend(newItem);
  }
  #removeBundle(btn) {
    --this.#countAdded;
    if (this.#countAdded < this.#min) {
      this.#add.setAttribute('disabled', '');
      this.#slot(this.#min - this.#countAdded).hidden = false;
      if (this.#bar) this.#bar.value = this.#countAdded;
    }
    this.#removeItem(this.#formData.items, { 'id': btn.dataset.id, 'quantity': btn.dataset.min });
    this.#total -= parseInt(btn.dataset.price);
    $4(`[ref="addBundle"][data-pr-id="${btn.dataset.prId}"]`)?.removeAttribute('added');
    this.#addBundles.forEach((_btn) => _btn.toggleAttribute('disabled', _btn.hasAttribute('added')));
    btn.closest('[ref="item"]').remove();
  }
  #createBundleItem(data, prId) {
    const clone = this.#tmp.content.cloneNode(true),
    imgEl = $4('[ref="img"]', clone),
    titleEl = $4('[ref="title"]', clone),
    optionsEl = $4('[ref="options"]', clone),
    moneyEl = $4('[ref="money"]', clone),
    removeEl = $4('[ref="removeBundle"]', clone),
    removeTextEl = $4('[ref="removeText"]', removeEl);

    if (imgEl) {
      imgEl.src = data.src || data.prSrc;
      imgEl.setAttribute('height', data.height || data.prHeight);
      imgEl.alt = data.title;
    }

    titleEl.textContent = data.title;
    moneyEl.innerHTML = currency.formatMoney(data.price);

    if (data.options) {
      optionsEl.textContent = data.options;
    } else {
      optionsEl.hidden = true;
    }
    removeEl.dataset.id = data.id;
    removeEl.dataset.prId = prId;
    removeEl.dataset.min = data.min;
    removeEl.dataset.price = data.price * data.min;
    removeTextEl.textContent = removeTextEl.textContent.replace('title94', data.title);

    return clone;
  }
  #slot(index) {
    return $4(`.hdt-bundle-slot-skeleton[slot="${index}"]`, this);
  }
  // Helper function: Checks if two items match in all key attributes (id, selling_plan, parent_id)
  #isSameItem(itemA, itemB) {
    return (
      itemA.id === itemB.id &&
      (itemA.selling_plan || null) === (itemB.selling_plan || null) &&
      (itemA.parent_id || null) === (itemB.parent_id || null)
    );
  }
  // Logic: ADD item (Increase quantity if item exists, otherwise add new item)
  #addItem(itemsList, newItem) {
    const addQty = newItem.quantity || 1;

    // Find if the item already exists in the list
    const existingItem = itemsList.find(item => this.#isSameItem(item, newItem));

    if (existingItem) {
      // Item exists -> Increase quantity
      existingItem.quantity += addQty;
    } else {
      // Item does not exist -> Push new item
      itemsList.push({ ...newItem, quantity: addQty });
    }

    return itemsList;
  }
  #removeItem(itemsList, targetItem) {
    const removeQty = targetItem.quantity || 1;

    // Find the index of the item
    const index = itemsList.findIndex(item => this.#isSameItem(item, targetItem));

    if (index !== -1) {
      // Decrease quantity
      itemsList[index].quantity -= removeQty;

      // Remove from array if quantity drops to 0 or below
      if (itemsList[index].quantity <= 0) {
        itemsList.splice(index, 1);
      }
    }

    return itemsList;
  }
  get #cart() {
    return $4('[ref="hdt-cart"]');
  }
  #updateJson(scriptEl, newData) {
    const currentData = JSON.parse(scriptEl.textContent);
    const updatedData = { ...currentData, ...newData };
    //console.log(currentData, newData, updatedData)
    scriptEl.textContent = JSON.stringify(updatedData);
    return updatedData;
  }
  #createImg(vimg, mediaMain) {
    return createImg({
      src: vimg,
      width: mediaMain.getAttribute('width'),
      height: mediaMain.getAttribute('height'),
      alt: mediaMain.alt,
      onload: true,
      attrs: {
        class: mediaMain.className,
        sizes: mediaMain.sizes,
        'data-widths': mediaMain.getAttribute('data-widths') || '[]'
      }
    })
  }
  #removeAllBundle() {
    // 1. Reset btn add bundle
    this.#addBundles.forEach((btn) => btn.removeAttribute('disabled'));

    // 2. Reset total price
    this.#totalEls.forEach( (el) => el.innerHTML = currency.formatMoney(0) );

    // 3. Reset items
    Array.from(this.#items.children).forEach((child) => {
      if (child.hasAttribute('slot')) {
        child.hidden = false;
      } else {
        child.remove();
      }
    });

    // 4. Reset btn add bundle to cart
    this.#add.setAttribute('disabled', '');

    // 5. Reset bar
    if (this.#bar) this.#bar.value = 0;

    // 6. Reset #formData
    this.#formData.items = [];

    // 7. Reset total price & countAdded
    this.#total = 0;
    this.#countAdded = 0;
  }
}
registerEl('hdt-product-bundle', ProductBundle);

class scrollSentinel extends HTMLElement {
  #page = 1;
  #totalPages;
  #popover;
  #observer;
  #isLoading = false;
  #controller;
  connectedCallback() {
    this.#popover = this.closest('.hdt-popover');
    if (!this.#popover || this.#observer) return;
    this.#controller = new AbortController();
    this.#popover.addEventListener(dialogOpen, () => {
      if (!this.#observer) this.#setupObserver();
    }, { once: true, signal: this.#controller.signal });
  }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }
  #setupObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.#isLoading) this.#fetchNext();
    }, {
      root: this.closest('.hdt-popover__inner'),
      threshold: 0,
      rootMargin: '600px'
    });
    this.#observer.observe(this);
  }
  #destroyObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.hidden = true;
  }
  async #fetchNext() {
    // Read here, not in the constructor: an element created from code has no attributes yet when it is constructed
    this.#totalPages ??= Math.ceil(parseInt(this.dataset.total) / 250) || 1;
    this.#isLoading = true;
    this.#popover.setAttribute('loading', '');
    try {
      const response = await fetch(`${this.dataset.url}?page=${this.#page}&section_id=options`);
      if (!response.ok) throw new Error(`Failed to load page ${this.#page}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html'),
      section = html.getElementById('shopify-section-options');
      if (this.#page >= this.#totalPages) {
        this.#destroyObserver();
      } else {
        this.#page++;
      }
      // Move the parsed options instead of parsing the HTML a second time
      if (section) this.before(...section.childNodes);
      // Still in view after the insert: the observer won't fire again by itself, so observe again to load the next page
      if (this.#observer) {
        this.#observer.unobserve(this);
        this.#observer.observe(this);
      }
    } catch (error) {
      // Network error: keep the page, it is tried again when the sentinel scrolls back into view
      console.error(error);
    } finally {
      this.#isLoading = false;
      this.#popover.removeAttribute('loading');
    }
  }
}
registerEl('hdt-scroll-sentinel', scrollSentinel);

/**
 * SHARKNINJA
 * Custom JS
**/

/* Notification icon animation */
// const svgNoti = document.querySelector('svg.notification-icon');

// if (localStorage.getItem('notification-icon-removed') === 'true') {
//   svgNoti.classList.remove('fa-swing');
// }

// svgNoti.addEventListener('click', function() {
//   this.classList.remove('fa-swing');
//   localStorage.setItem('notification-icon-removed', 'true');
// });

// SN - Cart drawer instalment message
class SnCartInstalment extends HTMLElement {
  connectedCallback() {
    this.footer = this.closest('.hdt-cart-drawer__footer');
    if (!this.footer) return;
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.footer, { childList: true });
    this.sync();
  }
  disconnectedCallback() {
    this.observer?.disconnect();
  }
  sync() {
    const source = $4('template[data-sn-instalment-source]', this.footer);
    if (source && this.innerHTML !== source.innerHTML) this.innerHTML = source.innerHTML;
  }
}
registerEl('sn-cart-instalment', SnCartInstalment);

// Comparison table - same height for each row
document.querySelectorAll('.sn-product-specs-full').forEach(el => {
  el.style.height = 'auto';
});

const groups = {};

document.querySelectorAll('.hdt-metafield-info').forEach(col => {
  col.querySelectorAll('.sn-product-specs-full, .sn-product-specs').forEach((row, i) => {
    groups[i] ??= [];
    groups[i].push(row);
  });
});

Object.values(groups).forEach(rows => {
  const max = Math.max(...rows.map(r => r.offsetHeight));
  rows.forEach(r => r.style.height = `${max}px`);
});

/* FIN - Add item to cart drawer */
document.addEventListener('intercom:cartUpdated', async () => {
  const res = await fetch(`${Shopify.routes.root}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) {
    doc.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
    doc.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };
  const cart = await res.json();
  document.dispatchEvent(new CustomEvent('cart:live', { detail: cart }));
  doc.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
});
